import { friendlyError } from '../../utils/userMessages';
import { brandPalette as palette, getContrastText, getReadableColor, getGradientContrastText } from '../../utils/brandPalette';
// src/components/dashboard/MeetingDashboard.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · Meeting Command Deck
//  Aesthetic: "Warm Liquid Glass" — with AI Intro & Connection Management
//  Updated: Fixed Agora Integration & Tab Data Issues
// ─────────────────────────────────────────────────────────────────────────────

import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Stack,
    Typography,
    Button,
    Avatar,
    Chip,
    IconButton,
    Skeleton,
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Tooltip,
    Tab,
    Tabs,
    InputAdornment,
    alpha,
    Alert,
    Badge,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    Switch,
    FormControlLabel,
    Collapse,
    Card,
    CardContent,
    CardActions,
} from '@mui/material';
import {
    Event as EventIcon,
    People as PeopleIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    Schedule as ScheduleIcon,
    FiberManualRecord as LiveIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    Timer as TimerIcon,
    Cancel as CancelIcon,
    CheckCircle as CheckCircleIcon,
    Visibility as VisibilityIcon,
    AutoAwesome as AiIcon,
    Dashboard as GridViewIcon,
    NotesOutlined as ListIcon,
    Email as InboxIcon,
    Send as OutboxIcon,
    Recommend as ThumbUpIcon,
    TrendingDown as ThumbDownIcon,
    Check as DoneIcon,
    Pending as PendingIcon,
    PersonAdd as GroupAddIcon,
    Schedule as AccessTimeIcon,
    Close as ClearIcon,
    VideoCall as VideoCallIcon,
    Schedule as UpcomingIcon,
    Restore as HistoryIcon,
    Assignment as RequestPageIcon,
    CalendarToday as CalendarIcon,
    LocationOn as LocationIcon,
    Public as PublicIcon,
    Message as MessageIcon,
    Group as GroupIcon,
    Menu as MoreVertIcon,
    Send as SendIcon,
    PersonAdd as PersonAddIcon,
    Business as BusinessIcon,
    Handshake as ConnectIcon,
    Handshake as HandshakeIcon,
    Chat as ChatIcon,
    Check as CheckIcon,
    Block as BlockIcon,
    EventNote as MeetingRoomIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../components/auth/AuthContext';
import Header from '../common/Header/Header';
import { db } from '../../config/firebase';
import {
    collection,
    getDocs,
    query,
    where,
    doc,
    updateDoc,
    addDoc,
    serverTimestamp,
    onSnapshot,
    getDoc,
    writeBatch,
    orderBy,
    limit,
    setDoc,
    getFirestore,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { generateIntroduction } from '../../services/integrations/ai';
import { introductionService, meetingWorkflowService } from '../../services/workflow/communication';
import { getCompanyCoverUrl, getCompanyLogoUrl, getPersonPhotoUrl } from '../../utils/companyIdentity';

// Resolve Framer Motion v12 Deprecation
const MotionDiv = motion.create ? motion.create('div') : motion.div;
const MotionSpan = motion.create ? motion.create('span') : motion.span;

/* ═══════════════════════════════════════════════════════════════════════════
   1 · DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */



const ACCENTS = [
    palette.rust,
    palette.teal,
    palette.amber,
    palette.pine,
    palette.plum,
    palette.indigo,
];

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,500&family=Instrument+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap');`;

const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return { r: 22, g: 18, b: 16 };
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return { r: 22, g: 18, b: 16 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const liquid = (accent = palette.rust, { depth = 1, radius = 18 } = {}) => ({
    position: 'relative',
    borderRadius: `${radius}px`,
    background: '#FFFFFF',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: '1px solid rgba(31,62,82,.075)',
    boxShadow: depth >= 3
        ? '0 16px 40px rgba(25,57,78,.12), 0 4px 12px rgba(25,57,78,.055)'
        : '0 5px 18px rgba(25,57,78,.055)',
    overflow: 'hidden',
    transition: 'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    }, []);
    const onPointerLeave = useCallback(() => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    }, []);
    return { ref, onPointerMove, onPointerLeave };
};

const sheenLayer = (accent) => ({
    content: '""',
    position: 'absolute',
    inset: '-50%',
    pointerEvents: 'none',
    opacity: 0.32,
    background: 'linear-gradient(115deg, transparent 35%, rgba(255,255,255,.45) 48%, transparent 62%)',
    transform: 'translateX(-60%)',
    animation: 'erpCardShine 5s ease-in-out infinite',
});

const glassWell = (radius = '14px', accent = null) => ({
    borderRadius: radius,
    background: accent ? alpha(accent, 0.055) : '#F4F8FB',
    border: `1px solid ${accent ? alpha(accent, 0.14) : 'rgba(31,62,82,.065)'}`,
    boxShadow: '0 2px 8px rgba(25,57,78,.03)',
});

/* ── Glass Ghost Button ──────────────────────────────────────────────────── */

const glassGhostButton = (color) => ({
    borderRadius: '10px',
    textTransform: 'none',
    fontWeight: 750,
    minHeight: 40,
    color,
    background: 'rgba(255,253,250,.95)',
    border: `1px solid ${alpha(color, .25)}`,
    boxShadow: '0 2px 7px rgba(25,57,78,.035)',
    transition: 'transform .2s ease, box-shadow .2s ease, background .2s ease',
    '&:hover': {
        transform: 'translateY(-1px)',
        background: alpha(color, .055),
        boxShadow: '0 8px 20px rgba(25,57,78,.08)',
    },
});

/* ── Glass Solid Button ──────────────────────────────────────────────────── */

const GlassSolidButton = ({ label, color, icon, onClick, variant = 'solid', disabled = false, size = 'small' }) => (
    <Button
        className="tf-meeting-colored-action"
        size={size}
        onClick={onClick}
        startIcon={icon}
        disabled={disabled}
        sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: size === 'small' ? '0.68rem' : '0.78rem',
            py: size === 'small' ? 0.6 : 0.8,
            px: size === 'small' ? 1.75 : 2.25,
            minHeight: size === 'small' ? 30 : 38,
            transition: 'all 0.28s cubic-bezier(0.34,1.56,0.64,1)',
            ...(variant === 'solid'
                ? {
                      color: getContrastText(color),
                      background: `linear-gradient(145deg, ${rgba(color, 0.96)}, ${color})`,
                      boxShadow: `0 8px 20px ${rgba(color, 0.3)}, 0 3px 8px ${rgba(color, 0.18)}, inset 0 1px 0 ${rgba('#FFFFFF', 0.6)}`,
                  }
                : {
                      color: getReadableColor(color),
                      background: `linear-gradient(145deg, ${rgba(color, 0.16)}, ${rgba(color, 0.08)})`,
                      backdropFilter: 'blur(10px) saturate(175%)',
                      WebkitBackdropFilter: 'blur(10px) saturate(175%)',
                      border: `1px solid ${rgba(color, 0.30)}`,
                      boxShadow: `0 6px 16px ${rgba(color, 0.16)}, inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}`,
                  }),
            '&:hover': { 
                transform: 'translateY(-2px)',
                ...(variant === 'solid' ? {
                    background: `linear-gradient(145deg, ${rgba(color, 1)}, ${rgba(color, 0.85)})`,
                } : {
                    background: rgba(color, 0.18),
                })
            },
            '&:active': { transform: 'translateY(0)' },
            '&.Mui-disabled': {
                opacity: 1,
                transform: 'none !important',
                color: '#6D7C91 !important',
                background: '#EEF3F6 !important',
                border: '1px solid rgba(31,62,82,.12) !important',
                boxShadow: 'none !important',
            },
        }}
    >
        {label}
    </Button>
);

/* ═══════════════════════════════════════════════════════════════════════════
   2 · FORMAT / MATH HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === 'function') {
        try { return value.toDate(); } catch { return null; }
    }
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
};

const formatDateTime = (value) => {
    const d = toDate(value);
    if (!d) return 'Date TBD';
    return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
};

const compact = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString();
};

const useCountUp = (target, duration = 900) => {
    const [display, setDisplay] = useState(0);
    const fromRef = useRef(0);

    useEffect(() => {
        const to = Number(target) || 0;
        const from = fromRef.current;
        if (from === to) { setDisplay(to); return undefined; }

        let raf;
        const start = performance.now();
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            const current = from + (to - from) * eased;
            fromRef.current = current;
            setDisplay(current);
            if (p < 1) raf = requestAnimationFrame(tick);
            else fromRef.current = to;
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);

    return display;
};

const STATUS_TONE = {
    completed: palette.success,
    confirmed: palette.pine,
    active: palette.pine,
    ongoing: palette.teal,
    scheduled: palette.teal,
    pending: palette.warning,
    cancelled: palette.danger,
    declined: palette.danger,
    rejected: palette.danger,
    in_progress: palette.rust,
    connected: palette.pine,
    disconnected: palette.danger,
};

const normalizeMeetingStatus = (status) => {
    const raw = String(status || 'pending').trim().toLowerCase().replace(/-/g, '_');
    if (['complete', 'completed', 'finished', 'done'].includes(raw)) return 'completed';
    if (['in progress', 'in_progress', 'inprogress', 'ongoing', 'live'].includes(raw)) return 'in_progress';
    if (['approved'].includes(raw)) return 'confirmed';
    return raw || 'pending';
};

const toneFor = (status) => STATUS_TONE[normalizeMeetingStatus(status)] || palette.inkLight;

/* ═══════════════════════════════════════════════════════════════════════════
   3 · LIQUID BACKDROP
   ═══════════════════════════════════════════════════════════════════════════ */

const LiquidBackdrop = React.memo(function LiquidBackdrop() {
    const motes = useMemo(
        () =>
            Array.from({ length: 18 }, (_, i) => ({
                id: i,
                size: 3 + ((i * 7) % 9),
                x: (i * 37) % 100,
                y: (i * 53) % 100,
                duration: 26 + ((i * 5) % 18),
                delay: (i * 1.7) % 12,
                color: ACCENTS[i % ACCENTS.length],
            })),
        []
    );

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <Box component="svg" sx={{ position: 'absolute', width: 0, height: 0 }} aria-hidden>
                <defs>
                    <filter id="tf-liquid-meeting">
                        <feTurbulence type="fractalNoise" baseFrequency="0.008 0.012" numOctaves="2" seed="7" result="noise">
                            <animate attributeName="baseFrequency" dur="34s" values="0.008 0.012;0.014 0.006;0.008 0.012" repeatCount="indefinite" />
                        </feTurbulence>
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="70" xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                </defs>
            </Box>

            <MotionDiv
                animate={{ x: [0, 70, -30, 0], y: [0, -45, 35, 0], scale: [1, 1.14, 0.9, 1] }}
                transition={{ duration: 30, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '-6%', right: '-4%',
                    width: 620, height: 620, borderRadius: '50%',
                    background: `radial-gradient(circle at 35% 35%, ${rgba(palette.rustGlow, 0.32)}, ${rgba(palette.rust, 0.14)} 45%, transparent 70%)`,
                    filter: 'url(#tf-liquid-meeting) blur(42px)',
                }}
            />
            <MotionDiv
                animate={{ x: [0, -55, 40, 0], y: [0, 40, -35, 0], scale: [1, 0.88, 1.16, 1] }}
                transition={{ duration: 36, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', bottom: '-10%', left: '-6%',
                    width: 560, height: 560, borderRadius: '50%',
                    background: `radial-gradient(circle at 60% 40%, ${rgba(palette.amberGlow, 0.26)}, ${rgba(palette.amber, 0.12)} 48%, transparent 72%)`,
                    filter: 'url(#tf-liquid-meeting) blur(48px)',
                }}
            />
            <MotionDiv
                animate={{ x: ['-50%', '-44%', '-56%', '-50%'], y: ['-50%', '-56%', '-45%', '-50%'], scale: [1, 1.08, 0.94, 1] }}
                transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '46%', left: '52%',
                    width: 760, height: 760, borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(palette.tealGlow, 0.16)}, transparent 66%)`,
                    filter: 'blur(80px)',
                }}
            />

            {motes.map((m) => (
                <MotionSpan
                    key={m.id}
                    initial={{ opacity: 0 }}
                    animate={{
                        y: [`${m.y}%`, `${m.y - 45}%`, `${m.y}%`],
                        x: [`${m.x}%`, `${m.x + 14}%`, `${m.x}%`],
                        opacity: [0, 0.5, 0],
                        scale: [0.4, 1, 0.4],
                    }}
                    transition={{ duration: m.duration, delay: m.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: m.size, height: m.size, borderRadius: '50%',
                        background: `radial-gradient(circle at 30% 30%, #fff, ${m.color})`,
                        boxShadow: `0 0 12px ${rgba(m.color, 0.5)}`,
                    }}
                />
            ))}

            <Box
                sx={{
                    position: 'absolute', inset: 0, opacity: 0.55,
                    backgroundImage: `linear-gradient(${rgba(palette.ink, 0.022)} 1px, transparent 1px),
                                      linear-gradient(90deg, ${rgba(palette.ink, 0.022)} 1px, transparent 1px)`,
                    backgroundSize: '72px 72px',
                    maskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 78%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 78%)',
                }}
            />
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   4 · PRIMITIVES & COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

const Rule = ({ color = palette.rust, w = 44 }) => (
    <Box
        sx={{
            width: w, height: 3, mt: 0.75, borderRadius: 3,
            background: `linear-gradient(90deg, ${color}, ${rgba(color, 0)})`,
            animation: 'tfRule 3.4s ease-in-out infinite',
        }}
    />
);

const GlassPanel = ({
    accent = palette.rust,
    delay = 0,
    lift = true,
    padding = { xs: 2, md: 2.5 },
    children,
    sx = {},
    ...rest
}) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    return (
        <MotionDiv
            initial={{ opacity: 0, y: 22, filter: 'blur(6px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: 'auto', width: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent),
                    p: padding,
                    height: 'auto',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    '&::after': sheenLayer(accent),
                    ...(lift && {
                        '&:hover': {
                            transform: 'translateY(-6px)',
                            borderColor: rgba('#FFFFFF', 0.85),
                            boxShadow: [
                                `inset 0 1px 0 ${rgba('#FFFFFF', 1)}`,
                                `inset 0 0 0 1px ${rgba(accent, 0.14)}`,
                                `0 6px 14px ${rgba(palette.ink, 0.06)}`,
                                `0 26px 56px -18px ${rgba(accent, 0.34)}`,
                            ].join(', '),
                        },
                    }),
                    ...sx,
                }}
                {...rest}
            >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Paper>
        </MotionDiv>
    );
};

const GlassIcon = ({ icon, color, size = 38 }) => (
    <Box
        sx={{
            width: size, height: size, flexShrink: 0,
            borderRadius: `${size * 0.34}px`,
            display: 'grid', placeItems: 'center',
            color: getContrastText(color),
            background: `linear-gradient(150deg, ${rgba(color, 0.95)}, ${rgba(color, 0.72)})`,
            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.55)}, inset 0 -2px 6px ${rgba(palette.ink, 0.18)}, 0 8px 20px -6px ${rgba(color, 0.55)}`,
            '& svg': { fontSize: size * 0.48, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.18))' },
        }}
    >
        {icon}
    </Box>
);

const EmptyState = ({ icon, text, action, onAction }) => (
    <Box sx={{ textAlign: 'center', py: 5, px: 2, flex: 1, display: 'grid', placeContent: 'center', ...glassWell(24) }}>
        <Box sx={{ display: 'grid', placeItems: 'center', mb: 1.5 }}>
            {React.cloneElement(icon, { sx: { fontSize: 40, color: palette.inkFaint } })}
        </Box>
        <Typography sx={{ color: palette.inkLight, fontSize: '0.88rem' }}>{text}</Typography>
        {action && (
            <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={onAction}
                sx={{
                    mt: 2, mx: 'auto', textTransform: 'none', fontWeight: 600,
                    borderRadius: '10px', px: 2.5, color: getGradientContrastText(palette.rust, palette.rustDeep),
                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                    boxShadow: `0 8px 20px -8px ${rgba(palette.rust, 0.7)}`,
                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                }}
            >
                {action}
            </Button>
        )}
    </Box>
);

/* ── Modern Stat Card ────────────────────────────────────────────────────── */

const ModernStatCard = ({ label, value, suffix = '', icon, overlayIcon, gradient, delta, hint, delay = 0, loading }) => {
    const animated = useCountUp(value);
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const trendColor =
        delta?.trend === 'up' ? palette.success : delta?.trend === 'down' ? palette.danger : palette.inkLight;
    const [gradientStart, gradientEnd] = gradient || [palette.rust, palette.rustGlow];

    return (
        <MotionDiv
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(gradientStart, { radius: 20, depth: 0.8 }),
                    height: '100%',
                    p: { xs: 1.75, md: 2 },
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': sheenLayer(gradientStart),
                    '&:hover': {
                        transform: 'translateY(-4px) scale(1.01)',
                        boxShadow: [
                            `inset 0 1px 0 ${rgba('#FFFFFF', 1)}`,
                            `inset 0 0 0 1px ${rgba(gradientStart, 0.16)}`,
                            `0 20px 48px -16px ${rgba(gradientStart, 0.4)}`,
                        ].join(', '),
                    },
                }}
            >
                {/* Background Watermark Icon */}
                {overlayIcon && (
                    <Box
                        sx={{
                            position: 'absolute',
                            right: -12,
                            bottom: -16,
                            opacity: 0.08,
                            transform: 'rotate(-15deg) scale(1.3)',
                            pointerEvents: 'none',
                            '& svg': { fontSize: 130, color: gradientStart },
                        }}
                    >
                        {overlayIcon}
                    </Box>
                )}

                {/* Animated gradient orb */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 140,
                        height: 140,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${rgba(gradientStart, 0.25)}, ${rgba(gradientEnd, 0.1)} 50%, transparent 70%)`,
                        filter: 'blur(30px)',
                        animation: 'tfOrbFloat 6s ease-in-out infinite',
                        pointerEvents: 'none',
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.8 }}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '12px',
                                display: 'grid',
                                placeItems: 'center',
                                background: `linear-gradient(150deg, ${rgba(gradientStart, 0.2)}, ${rgba(gradientEnd, 0.08)})`,
                                border: `1px solid ${rgba(gradientStart, 0.15)}`,
                                backdropFilter: 'blur(8px)',
                                '& svg': { fontSize: 18, color: gradientStart },
                            }}
                        >
                            {icon}
                        </Box>
                        {delta && (
                            <Chip
                                size="small"
                                icon={
                                    delta.trend === 'down'
                                        ? <TrendingDownIcon sx={{ fontSize: 11 }} />
                                        : <TrendingUpIcon sx={{ fontSize: 11 }} />
                                }
                                label={delta.label}
                                sx={{
                                    height: 20,
                                    fontFamily: fontMono,
                                    fontSize: '0.55rem',
                                    fontWeight: 700,
                                    color: trendColor,
                                    bgcolor: rgba(trendColor, 0.08),
                                    border: `1px solid ${rgba(trendColor, 0.15)}`,
                                    '& .MuiChip-icon': { color: trendColor, ml: '4px', mr: '2px' },
                                    '& .MuiChip-label': { px: 1 },
                                }}
                            />
                        )}
                    </Box>

                    {loading ? (
                        <Skeleton width="50%" height={32} sx={{ bgcolor: palette.hairline }} />
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: fontDisplay,
                                fontWeight: 700,
                                fontSize: { xs: '1.6rem', md: '1.9rem' },
                                lineHeight: 1,
                                letterSpacing: '-0.03em',
                                background: `linear-gradient(140deg, ${palette.ink} 20%, ${gradientStart})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {suffix === '%' ? `${Math.round(animated)}%` : compact(animated)}
                            {suffix && suffix !== '%' && (
                                <Box component="span" sx={{ fontSize: '0.5em', ml: 0.3, opacity: 0.6 }}>{suffix}</Box>
                            )}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            mt: 0.5,
                            fontFamily: fontMono,
                            fontSize: '0.58rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            color: palette.inkLight,
                        }}
                    >
                        {label}
                    </Typography>

                    {hint && (
                        <Typography
                            sx={{
                                mt: 'auto',
                                pt: 1.25,
                                fontSize: '0.62rem',
                                color: palette.inkLight,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.6,
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 5,
                                    height: 5,
                                    borderRadius: '50%',
                                    bgcolor: gradientStart,
                                    boxShadow: `0 0 0 3px ${rgba(gradientStart, 0.15)}`,
                                    animation: 'tfPulse 2.4s ease-in-out infinite',
                                }}
                            />
                            {hint}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </MotionDiv>
    );
};

/* ── Glass Pill ───────────────────────────────────────────────────────────── */

const GlassPill = ({ label, color = palette.rust, icon, size = 'sm', filled = false }) => (
    <Box
        sx={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            borderRadius: '999px', fontWeight: 600, whiteSpace: 'nowrap',
            backdropFilter: 'blur(8px) saturate(170%)',
            WebkitBackdropFilter: 'blur(8px) saturate(170%)',
            height: size === 'sm' ? 20 : 24,
            px: size === 'sm' ? 0.8 : 1.2,
            fontSize: size === 'sm' ? '0.55rem' : '0.62rem',
            letterSpacing: '0.03em',
            fontFamily: fontMono,
            ...(filled
                ? {
                      color: getContrastText(color),
                      background: `linear-gradient(150deg, ${rgba(color, 0.92)}, ${rgba(color, 0.7)})`,
                      border: `1px solid ${rgba('#FFFFFF', 0.45)}`,
                      boxShadow: `0 4px 10px ${rgba(color, 0.25)}, inset 0 1px 0 ${rgba('#FFFFFF', 0.6)}`,
                  }
                : {
                      color,
                      background: rgba(color, 0.08),
                      border: `1px solid ${rgba(color, 0.2)}`,
                      boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.6)}`,
                  }),
        }}
    >
        {icon}
        {label}
    </Box>
);

/* ── Meeting Status Chip ────────────────────────────────────────────────── */

const MeetingStatusChip = ({ status }) => {
    const statusConfig = {
        pending: { label: 'Pending', color: palette.amber, icon: <PendingIcon sx={{ fontSize: 14 }} /> },
        confirmed: { label: 'Confirmed', color: palette.pine, icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
        scheduled: { label: 'Scheduled', color: palette.teal, icon: <ScheduleIcon sx={{ fontSize: 14 }} /> },
        completed: { label: 'Completed', color: palette.pine, icon: <DoneIcon sx={{ fontSize: 14 }} /> },
        cancelled: { label: 'Cancelled', color: palette.danger, icon: <CancelIcon sx={{ fontSize: 14 }} /> },
        in_progress: { label: 'In Progress', color: palette.rust, icon: <LiveIcon sx={{ fontSize: 14 }} /> },
        declined: { label: 'Declined', color: palette.danger, icon: <CancelIcon sx={{ fontSize: 14 }} /> },
        accepted: { label: 'Accepted', color: palette.pine, icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
        connected: { label: 'Connected', color: palette.pine, icon: <HandshakeIcon sx={{ fontSize: 14 }} /> },
        disconnected: { label: 'Disconnected', color: palette.danger, icon: <BlockIcon sx={{ fontSize: 14 }} /> },
        ai_intro: { label: 'AI Intro', color: palette.plum, icon: <AiIcon sx={{ fontSize: 14 }} /> },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
        <Chip
            icon={config.icon}
            label={config.label}
            size="small"
            sx={{
                bgcolor: `${config.color}12`,
                color: config.color,
                fontWeight: 600,
                height: 24,
                border: `1px solid ${config.color}25`,
                fontFamily: fontBody,
                '& .MuiChip-icon': { color: config.color }
            }}
        />
    );
};

/* ── Company Users Tab with AI Intro ───────────────────────────────────── */

const CompanyUsersTab = ({ companyId, onInviteUser, onSendAIIntro, sentRequests, onScheduleMeeting, meetings = [], onAddUserToMeeting }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [aiIntroDialogOpen, setAiIntroDialogOpen] = useState(false);
    const [aiIntroMessage, setAiIntroMessage] = useState('');
    const [sendingIntro, setSendingIntro] = useState(false);
    const [generatingAI, setGeneratingAI] = useState(false);
    const [meetingPickerOpen, setMeetingPickerOpen] = useState(false);
    const [meetingPickerUser, setMeetingPickerUser] = useState(null);
    const [addingToMeeting, setAddingToMeeting] = useState(false);
    const [listedCompany, setListedCompany] = useState(null);
    const { userData, currentUser, companyData } = useAuth();

    useEffect(() => {
        if (companyId) {
            fetchUsers();
            getDoc(doc(db, 'companies', String(companyId)))
                .then((snap) => setListedCompany(snap.exists() ? { id: snap.id, ...snap.data() } : null))
                .catch(() => setListedCompany(null));
        } else {
            setListedCompany(null);
        }
    }, [companyId]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('companyId', '==', companyId));
            const querySnapshot = await getDocs(q);
            const usersList = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                usersList.push({
                    id: doc.id,
                    name: data.displayName || data.firstName || data.name || 'Unnamed User',
                    email: data.email || '',
                    role: data.role || 'Employee',
                    department: data.department || '',
                    designation: data.designation || '',
                    isBlocked: data.isBlocked || false,
                    isOnline: data.isOnline || false,
                    lastActive: data.lastActive || null,
                    photoURL: data.photoURL || null,
                    companyName: data.companyName || data.company || '',
                    ...data
                });
            });
            setUsers(usersList);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to load company users');
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = users.filter(user => 
        !user.isBlocked &&
        (user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         user.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         user.department?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleAIIntroClick = (user) => {
        setSelectedUser(user);
        setAiIntroDialogOpen(true);
        generateAIIntro(user);
    };

    const generateAIIntro = async (user) => {
        setGeneratingAI(true);
        try {
            const targetUserId = user?.uid || user?.userId || user?.id;
            const result = await generateIntroduction({
                companyId,
                targetUserId,
                context: { source: 'meeting_dashboard_company_users' },
                tone: 'professional',
            });
            if (!result?.text) throw new Error('AI returned an empty introduction.');
            setAiIntroMessage(result.text);
        } catch (error) {
            console.error('Error generating ChatGPT intro:', error);
            const companyName = companyData?.companyName || 'our company';
            setAiIntroMessage(`Hello ${user?.name || ''}, I'm ${userData?.displayName || 'a TELCOFAIR member'} from ${companyName}. I would like to explore potential business opportunities with you. If this is relevant, please choose whether you would prefer to start a chat or share your calendar.`);
            toast('ChatGPT AI is unavailable. A standard editable draft was created.', { icon: '✨' });
        } finally {
            setGeneratingAI(false);
        }
    };

    const handleSendAIIntro = async () => {
        if (!selectedUser || !aiIntroMessage.trim()) {
            toast.error('Please enter a message');
            return;
        }
        setSendingIntro(true);
        try {
            await onSendAIIntro(selectedUser, aiIntroMessage);
            setAiIntroDialogOpen(false);
            setAiIntroMessage('');
            setSelectedUser(null);
        } catch (error) {
            console.error('Error sending AI intro:', error);
            toast.error('Failed to send introduction');
        } finally {
            setSendingIntro(false);
        }
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.split(' ');
        return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
    };

    const isRequestSent = (userId) => {
        return sentRequests.some(req => req.targetUserId === userId && req.status === 'pending');
    };

    const isConnected = (userId) => {
        return sentRequests.some(req => req.targetUserId === userId && req.status === 'accepted');
    };

    const isRequestRejected = (userId) => {
        return sentRequests.some(req => req.targetUserId === userId && req.status === 'rejected');
    };

    const eligibleMeetings = (meetings || []).filter((meeting) => {
        const status = String(meeting?.status || '').toLowerCase();
        return meeting?.id && !['cancelled', 'completed', 'declined', 'rejected'].includes(status);
    });

    const openMeetingPicker = (user) => {
        setMeetingPickerUser(user);
        setMeetingPickerOpen(true);
    };

    const addUserToExistingMeeting = async (meeting) => {
        if (!meetingPickerUser || !meeting || !onAddUserToMeeting) return;
        setAddingToMeeting(true);
        try {
            await onAddUserToMeeting(meeting, meetingPickerUser);
            setMeetingPickerOpen(false);
            setMeetingPickerUser(null);
        } catch (error) {
            console.error('Failed to add user to meeting:', error);
        } finally {
            setAddingToMeeting(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                <TextField
                    placeholder="Search team members..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    sx={{
                        flex: 1,
                        minWidth: 200,
                        '& .MuiOutlinedInput-root': {
                            ...glassWell(12),
                            borderRadius: '12px',
                            fontSize: '0.82rem',
                            '& fieldset': { border: 'none' },
                        }
                    }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: palette.inkFaint, fontSize: 18 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                                        <ClearIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }
                    }}
                />
                <GlassPill
                    label={`${filteredUsers.length} Team Members`}
                    color={palette.teal}
                    icon={<PeopleIcon sx={{ fontSize: 12 }} />}
                    filled
                />
            </Box>

            {loading ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: '14px', bgcolor: palette.hairline }} />
                    ))}
                </Box>
            ) : filteredUsers.length === 0 ? (
                <EmptyState
                    icon={<PeopleIcon />}
                    text={searchTerm ? 'No team members match your search.' : 'No team members found.'}
                />
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                    {filteredUsers.map((user, userIndex) => {
                        const requestSent = isRequestSent(user.id);
                        const connected = isConnected(user.id);
                        const rejected = isRequestRejected(user.id);
                        const isCurrentUser = user.id === currentUser?.uid || user.id === userData?.uid;
                        const userAccent = ACCENTS[userIndex % ACCENTS.length];
                        const personPhoto = getPersonPhotoUrl(user);
                        const listedCover = getCompanyCoverUrl(listedCompany || {});
                        const listedLogo = getCompanyLogoUrl(listedCompany || {});

                        return (
                            <MotionDiv
                                key={user.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Paper
                                    elevation={0}
                                    className="tf-meeting-person-card"
                                    sx={{
                                        '--meeting-person-color': userAccent,
                                        ...glassWell(14),
                                        p: 1.75,
                                        pt: listedCover ? 8.2 : 1.75,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        background: listedCover ? `linear-gradient(180deg,rgba(18,35,52,.06) 0,rgba(18,35,52,.18) 72px,#fff 72px,#fff 100%), url("${listedCover}") top center/100% 72px no-repeat !important` : undefined,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        transition: 'all 0.3s ease',
                                        opacity: rejected ? 0.5 : 1,
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            borderColor: rgba(palette.rust, 0.2),
                                        },
                                    }}
                                >
                                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                        <Badge
                                            overlap="circular"
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            variant="dot"
                                            color={user.isOnline ? 'success' : 'error'}
                                            sx={{ '& .MuiBadge-badge': { boxShadow: `0 0 0 2px #fff`, width: 12, height: 12, borderRadius: '50%' } }}
                                        >
                                            <Avatar
                                                src={personPhoto || undefined}
                                                sx={{ width: 50, height: 50, borderRadius: '13px', bgcolor: userAccent, color: getContrastText(userAccent), fontWeight: 600, fontFamily: fontDisplay, fontSize: '0.9rem', border: '3px solid #fff', boxShadow: `0 8px 18px ${rgba(userAccent,.22)}` }}
                                            >
                                                {getInitials(user.name)}
                                            </Avatar>
                                        </Badge>
                                        {listedLogo && <Avatar src={listedLogo} variant="rounded" sx={{ position:'absolute',right:-7,bottom:-5,width:21,height:21,borderRadius:'6px',bgcolor:'#fff',border:'2px solid #fff',boxShadow:'0 4px 9px rgba(25,57,78,.16)','& img':{objectFit:'contain'} }} />}
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                            <Typography sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.82rem' }} noWrap>
                                                {user.name}
                                            </Typography>
                                            {connected && (
                                                <GlassPill
                                                    label="Connected"
                                                    color={palette.pine}
                                                    size="sm"
                                                    icon={<HandshakeIcon sx={{ fontSize: 10 }} />}
                                                    filled
                                                />
                                            )}
                                            {requestSent && !connected && !rejected && (
                                                <GlassPill
                                                    label="Pending"
                                                    color={palette.amber}
                                                    size="sm"
                                                    icon={<PendingIcon sx={{ fontSize: 10 }} />}
                                                    filled
                                                />
                                            )}
                                            {rejected && (
                                                <GlassPill
                                                    label="Declined"
                                                    color={palette.danger}
                                                    size="sm"
                                                    icon={<BlockIcon sx={{ fontSize: 10 }} />}
                                                    filled
                                                />
                                            )}
                                        </Box>
                                        <Typography sx={{ color: palette.inkLight, fontSize: '0.68rem' }} noWrap>
                                            {user.designation || user.role || 'Team Member'}
                                            {user.department && ` • ${user.department}`}
                                        </Typography>
                                        <Typography sx={{ color: palette.inkFaint, fontSize: '0.6rem' }} noWrap>
                                            {user.email}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                                        {!isCurrentUser && eligibleMeetings.length > 0 && (
                                            <Tooltip title="Add to an existing meeting">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openMeetingPicker(user)}
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        borderRadius: '9px',
                                                        color: getGradientContrastText(userAccent, palette.teal),
                                                        background: `linear-gradient(145deg, ${userAccent}, ${palette.teal})`,
                                                        boxShadow: `0 7px 16px ${rgba(userAccent, .24)}`,
                                                        '&:hover': { transform: 'translateY(-1px)', background: `linear-gradient(145deg, ${userAccent}, ${palette.teal})` },
                                                    }}
                                                >
                                                    <PersonAddIcon sx={{ fontSize: 16, color: 'inherit' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {!isCurrentUser && !rejected && !connected && !requestSent && (
                                            <Tooltip title="Send AI Introduction">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleAIIntroClick(user)}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '10px',
                                                        color: userAccent,
                                                        bgcolor: rgba(userAccent, 0.09),
                                                        '&:hover': {
                                                            bgcolor: rgba(palette.plum, 0.16),
                                                            transform: 'scale(1.05)',
                                                        },
                                                        transition: 'all 0.3s ease',
                                                    }}
                                                >
                                                    <AiIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {connected && (
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title="Chat">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onInviteUser(user)}
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: '8px',
                                                            color: palette.teal,
                                                            bgcolor: rgba(palette.teal, 0.08),
                                                            '&:hover': {
                                                                bgcolor: rgba(palette.teal, 0.16),
                                                            },
                                                        }}
                                                    >
                                                        <ChatIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Schedule Meeting">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onScheduleMeeting(user)}
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: '8px',
                                                            color: palette.rust,
                                                            bgcolor: rgba(palette.rust, 0.08),
                                                            '&:hover': {
                                                                bgcolor: rgba(palette.rust, 0.16),
                                                            },
                                                        }}
                                                    >
                                                        <EventIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>
                            </MotionDiv>
                        );
                    })}
                </Box>
            )}

            <Dialog
                open={meetingPickerOpen}
                onClose={() => !addingToMeeting && setMeetingPickerOpen(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: { sx: { ...liquid(palette.teal, { radius: 22 }), overflow: 'hidden' } },
                    backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: rgba(palette.ink, 0.28) } },
                }}
            >
                <Box sx={{ p: 2, color: getGradientContrastText(palette.rust, palette.teal), background: `linear-gradient(118deg, ${palette.rust}, ${palette.teal})`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: '11px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.22)' }}>
                            <PersonAddIcon sx={{ color: 'inherit', fontSize: 19 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ color: 'inherit', fontWeight: 850, fontSize: '.95rem' }}>Add Member to Meeting</Typography>
                            <Typography sx={{ color: 'inherit', opacity: .76, fontSize: '.68rem' }}>{meetingPickerUser?.name || 'Team member'} can be added to any active meeting below.</Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={() => setMeetingPickerOpen(false)} disabled={addingToMeeting} sx={{ color:'inherit', bgcolor:'rgba(255,255,255,.18)' }}><CloseIcon sx={{ fontSize:17 }} /></IconButton>
                </Box>
                <DialogContent sx={{ p: 1.5 }}>
                    {eligibleMeetings.length === 0 ? (
                        <EmptyState icon={<EventIcon />} text="No active meetings are available. Create or confirm a meeting first." />
                    ) : (
                        <Box sx={{ display:'grid', gap:1 }}>
                            {eligibleMeetings.map((meeting, index) => {
                                const memberIds = (meeting.participantIds || []).map(String);
                                const alreadyAdded = memberIds.includes(String(meetingPickerUser?.id || meetingPickerUser?.uid || '')) || (meeting.participants || []).some((p) => String(p?.id || p?.userId || p) === String(meetingPickerUser?.id || meetingPickerUser?.uid || ''));
                                const tone = ACCENTS[index % ACCENTS.length];
                                return (
                                    <Paper key={meeting.id} elevation={0} className="tf-interactive-card" sx={{ p:1.25, display:'flex', alignItems:'center', gap:1.2, border:`1px solid ${rgba(tone,.14)}`, background:`linear-gradient(145deg,#fff,${rgba(tone,.045)}) !important` }}>
                                        <Box sx={{ width:38, height:38, borderRadius:'11px', flexShrink:0, display:'grid', placeItems:'center', color:'#fff', background:`linear-gradient(145deg,${tone},${rgba(tone,.68)})`, boxShadow:`0 7px 16px ${rgba(tone,.22)}` }}><EventIcon sx={{ fontSize:18, color:'#fff' }} /></Box>
                                        <Box sx={{ flex:1, minWidth:0 }}>
                                            <Typography sx={{ fontWeight:800, color:palette.ink, fontSize:'.78rem' }} noWrap>{meeting.title || 'Business Meeting'}</Typography>
                                            <Typography sx={{ color:palette.inkLight, fontSize:'.62rem' }} noWrap>{meeting.targetCompany || meeting.requesterCompany || 'TELCOFAIR'} • {formatDateTime(meeting.scheduledTime)}</Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            disabled={addingToMeeting || alreadyAdded}
                                            onClick={() => addUserToExistingMeeting(meeting)}
                                            startIcon={alreadyAdded ? <CheckIcon sx={{ fontSize:13 }} /> : <PersonAddIcon sx={{ fontSize:13 }} />}
                                            sx={{ color:'#fff !important', background: alreadyAdded ? `${palette.success} !important` : `linear-gradient(135deg,${tone},${palette.teal}) !important`, minWidth:100 }}
                                        >
                                            {alreadyAdded ? 'Added' : 'Add'}
                                        </Button>
                                    </Paper>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* AI Intro Dialog - Keep existing */}
            <Dialog
                open={aiIntroDialogOpen}
                onClose={() => setAiIntroDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            ...liquid(palette.plum, { radius: 24 }),
                            background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 60%, ${rgba(palette.plum, 0.06)})`,
                        },
                    },
                    backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: rgba(palette.ink, 0.28) } }
                }}
            >
                <Box
                    sx={{
                        p: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                        <GlassIcon icon={<AiIcon />} color={palette.plum} size={44} />
                        <Box>
                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.1rem', color: palette.ink }}>
                                AI Introduction
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.72rem' }}>
                                Send an AI-generated introduction to {selectedUser?.name}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setAiIntroDialogOpen(false)}
                        sx={{
                            width: 32, height: 32, borderRadius: '10px', color: palette.inkSoft,
                            bgcolor: rgba(palette.ink, 0.04),
                            '&:hover': { bgcolor: rgba(palette.ink, 0.08), transform: 'rotate(90deg)' },
                            transition: 'transform .3s ease, background .3s ease',
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>

                <DialogContent sx={{ px: 2.5, py: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, p: 1.5, ...glassWell(12) }}>
                        <Avatar
                            src={selectedUser?.photoURL}
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '12px',
                                bgcolor: palette.plum,
                                color: getContrastText(palette.plum),
                                fontWeight: 600,
                                fontSize: '1rem',
                            }}
                        >
                            {selectedUser ? getInitials(selectedUser.name) : 'U'}
                        </Avatar>
                        <Box>
                            <Typography sx={{ fontWeight: 600, color: palette.ink }}>
                                {selectedUser?.name || 'User'}
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                                {selectedUser?.designation || selectedUser?.role || 'Team Member'}
                                {selectedUser?.companyName && ` at ${selectedUser.companyName}`}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.85rem' }}>
                            AI-Generated Introduction
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => generateAIIntro(selectedUser)}
                            disabled={generatingAI}
                            sx={{ 
                                color: palette.plum, 
                                fontWeight: 600, 
                                textTransform: 'none',
                                fontSize: '0.72rem'
                            }}
                        >
                            {generatingAI ? <CircularProgress size={16} sx={{ color: palette.plum }} /> : 'Regenerate'}
                        </Button>
                    </Box>

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Your introduction message..."
                        value={aiIntroMessage}
                        onChange={(e) => setAiIntroMessage(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                ...glassWell(12),
                                borderRadius: '12px',
                                fontSize: '0.9rem',
                                lineHeight: 1.6,
                            }
                        }}
                    />

                    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', bgcolor: rgba(palette.amber, 0.06), border: `1px solid ${rgba(palette.amber, 0.15)}` }}>
                        <Typography sx={{ fontSize: '0.72rem', color: palette.inkLight, display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                            <Box component="span" sx={{ mt: 0.25 }}>💡</Box>
                            <Box>
                                <strong>AI Tip:</strong> This introduction will be sent to {selectedUser?.name}. 
                                They will have the option to accept or decline. Once accepted, you'll be able to chat and schedule meetings.
                            </Box>
                        </Typography>
                    </Box>
                </DialogContent>

                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1,
                        borderTop: `1px solid ${palette.hairlineSoft}`,
                        background: rgba('#FFFFFF', 0.45),
                        backdropFilter: 'blur(8px)',
                    }}
                >
                    <Button
                        onClick={() => setAiIntroDialogOpen(false)}
                        sx={{ textTransform: 'none', fontWeight: 600, color: palette.inkSoft, borderRadius: '10px', px: 1.75, fontSize: '0.78rem' }}
                    >
                        Cancel
                    </Button>
                    <GlassSolidButton
                        label={sendingIntro ? 'Sending...' : 'Send AI Introduction'}
                        color={palette.plum}
                        icon={sendingIntro ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 14 }} />}
                        onClick={handleSendAIIntro}
                        disabled={!aiIntroMessage.trim() || sendingIntro}
                    />
                </Box>
            </Dialog>
        </Box>
    );
};

/* ── AI Intro Requests Tab ────────────────────────────────────────────── */

const AIIntroRequestsTab = ({ requests, onAccept, onReject, onChat, onSchedule, currentUserId }) => {
    const [expandedRequest, setExpandedRequest] = useState(null);
    const [availabilityRequest, setAvailabilityRequest] = useState(null);
    const [availabilityRows, setAvailabilityRows] = useState([]);
    const [sharingAvailability, setSharingAvailability] = useState(false);

    const newAvailabilityRow = (offsetDays = 1) => {
        const date = new Date();
        date.setDate(date.getDate() + offsetDays);
        return { date: date.toISOString().slice(0, 10), start: '10:00', end: '12:00' };
    };

    const openAvailabilityDialog = (request) => {
        const existing = Array.isArray(request?.sharedAvailability) ? request.sharedAvailability : [];
        const rows = existing.slice(0, 7).map((window) => {
            const start = new Date(window.start);
            const end = new Date(window.end);
            return Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())
                ? null
                : {
                    date: start.toISOString().slice(0, 10),
                    start: start.toTimeString().slice(0, 5),
                    end: end.toTimeString().slice(0, 5),
                };
        }).filter(Boolean);
        setAvailabilityRequest(request);
        setAvailabilityRows(rows.length ? rows : [newAvailabilityRow(1)]);
    };

    const updateAvailabilityRow = (index, field, value) => {
        setAvailabilityRows((rows) => rows.map((row, idx) => idx === index ? { ...row, [field]: value } : row));
    };

    const shareSelectedAvailability = async () => {
        if (!availabilityRequest) return;
        const windows = availabilityRows.map((row) => {
            const start = new Date(`${row.date}T${row.start}:00`);
            const end = new Date(`${row.date}T${row.end}:00`);
            if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start || start <= new Date()) return null;
            return { start: start.toISOString(), end: end.toISOString() };
        }).filter(Boolean);
        if (!windows.length) {
            toast.error('Add at least one future availability window with an end time after the start time.');
            return;
        }
        setSharingAvailability(true);
        try {
            const ok = await onAccept(availabilityRequest, 'calendar', { availability: windows });
            if (ok !== false) {
                setAvailabilityRequest(null);
                setAvailabilityRows([]);
            }
        } finally {
            setSharingAvailability(false);
        }
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'pending': return palette.amber;
            case 'accepted': return palette.pine;
            case 'rejected': return palette.danger;
            default: return palette.inkLight;
        }
    };

    const getStatusLabel = (status) => {
        switch(status) {
            case 'pending': return 'Awaiting Response';
            case 'accepted': return 'Connected';
            case 'rejected': return 'Declined';
            default: return status || 'Unknown';
        }
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'pending': return <PendingIcon sx={{ fontSize: 14 }} />;
            case 'accepted': return <HandshakeIcon sx={{ fontSize: 14 }} />;
            case 'rejected': return <BlockIcon sx={{ fontSize: 14 }} />;
            default: return <PendingIcon sx={{ fontSize: 14 }} />;
        }
    };

    return (
        <Box>
            {requests.length === 0 ? (
                <EmptyState
                    icon={<ConnectIcon />}
                    text="No AI introduction requests yet."
                />
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {requests.map((request) => {
                        const isPending = request.status === 'pending';
                        const isAccepted = request.status === 'accepted';
                        const isRejected = request.status === 'rejected';
                        const isReceived = request.targetUserId === currentUserId;
                        const statusColor = getStatusColor(request.status);

                        return (
                            <MotionDiv
                                key={request.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        ...liquid(statusColor, { radius: 18, depth: 0.5 }),
                                        p: 2,
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: 2,
                                            background: `linear-gradient(90deg, ${statusColor}, ${statusColor}60)`,
                                        }}
                                    />

                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                                            <Avatar
                                                src={request.senderPhotoURL}
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: '12px',
                                                    bgcolor: palette.plum,
                                                    color: getContrastText(palette.plum),
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {(request.senderName || 'S').charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                    <Typography sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.9rem' }} noWrap>
                                                        {request.senderName || 'Unknown User'}
                                                    </Typography>
                                                    <Chip
                                                        icon={getStatusIcon(request.status)}
                                                        label={getStatusLabel(request.status)}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            bgcolor: `${statusColor}15`,
                                                            color: statusColor,
                                                            fontWeight: 600,
                                                            fontSize: '0.55rem',
                                                            border: `1px solid ${statusColor}25`,
                                                            '& .MuiChip-icon': { color: statusColor, fontSize: 12 },
                                                        }}
                                                    />
                                                    {request.senderCompany && (
                                                        <Typography sx={{ color: palette.inkLight, fontSize: '0.68rem' }} noWrap>
                                                            from {request.senderCompany}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Typography sx={{ color: palette.inkLight, fontSize: '0.72rem' }}>
                                                    {formatDateTime(request.createdAt)}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {isPending && isReceived && (
                                            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                                                <GlassSolidButton
                                                    label="Start Chat"
                                                    color={palette.pine}
                                                    icon={<ChatIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => onAccept(request, 'chat')}
                                                    size="small"
                                                />
                                                <GlassSolidButton
                                                    label="Share My Calendar"
                                                    color={palette.rust}
                                                    icon={<CalendarIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => openAvailabilityDialog(request)}
                                                    size="small"
                                                />
                                                <GlassSolidButton
                                                    label="Ignore"
                                                    color={palette.danger}
                                                    variant="soft"
                                                    icon={<BlockIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => onReject(request)}
                                                    size="small"
                                                />
                                            </Box>
                                        )}

                                        {isAccepted && (
                                            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                                                {request.chatEnabled && <GlassSolidButton
                                                    label="Chat"
                                                    color={palette.teal}
                                                    icon={<ChatIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => onChat(request)}
                                                    size="small"
                                                />}
                                                {request.calendarShared && <GlassSolidButton
                                                    label="Book Calendar"
                                                    color={palette.rust}
                                                    icon={<EventIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => onSchedule(request)}
                                                    size="small"
                                                />}
                                                {isReceived && request.meetingRequestPending && !request.calendarShared && <GlassSolidButton
                                                    label="Share Availability"
                                                    color={palette.rust}
                                                    icon={<CalendarIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => openAvailabilityDialog(request)}
                                                    size="small"
                                                />}
                                            </Box>
                                        )}

                                        {isRejected && (
                                            <GlassPill
                                                label="Request Declined"
                                                color={palette.danger}
                                                icon={<BlockIcon sx={{ fontSize: 12 }} />}
                                                filled
                                            />
                                        )}
                                    </Box>

                                    {request.message && (
                                        <Box
                                            sx={{
                                                mt: 1.5,
                                                p: 1.5,
                                                borderRadius: '12px',
                                                bgcolor: rgba(palette.ink, 0.03),
                                                border: `1px solid ${palette.hairline}`,
                                            }}
                                        >
                                            <Typography sx={{ color: palette.inkSoft, fontSize: '0.82rem', lineHeight: 1.6 }}>
                                                {request.message}
                                            </Typography>
                                        </Box>
                                    )}

                                    {Array.isArray(request.sharedAvailability) && request.sharedAvailability.length > 0 && (
                                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap' }}>
                                            <GlassPill label={`${request.sharedAvailability.length} shared availability window${request.sharedAvailability.length === 1 ? '' : 's'}`} color={palette.rust} icon={<CalendarIcon sx={{ fontSize: 12 }} />} />
                                        </Box>
                                    )}

                                    {request.remarks && (
                                        <Box
                                            sx={{
                                                mt: 1,
                                                p: 1.25,
                                                borderRadius: '10px',
                                                bgcolor: rgba(palette.pine, 0.04),
                                                border: `1px solid ${rgba(palette.pine, 0.15)}`,
                                            }}
                                        >
                                            <Typography sx={{ color: palette.pine, fontSize: '0.72rem', fontWeight: 600 }}>
                                                Remarks:
                                            </Typography>
                                            <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem', lineHeight: 1.5 }}>
                                                {request.remarks}
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </MotionDiv>
                        );
                    })}
                </Box>
            )}

            <Dialog
                open={Boolean(availabilityRequest)}
                onClose={() => !sharingAvailability && setAvailabilityRequest(null)}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: { sx: { borderRadius: '18px', overflow: 'hidden' } } }}
            >
                <Box sx={{ p: 2.25, borderBottom: `1px solid ${palette.hairline}`, background: `linear-gradient(135deg, ${rgba(palette.rust, .08)}, ${rgba(palette.teal, .04)})` }}>
                    <Typography sx={{ color: palette.ink, fontWeight: 800, fontSize: '1rem' }}>Choose availability to share</Typography>
                    <Typography sx={{ color: palette.inkLight, fontSize: '0.72rem', mt: 0.35 }}>
                        Share only the windows you want this contact to see. Times use your current device timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time'}).
                    </Typography>
                </Box>
                <DialogContent sx={{ p: 2.25 }}>
                    <Stack spacing={1.1}>
                        {availabilityRows.map((row, index) => (
                            <Box key={`${row.date}-${index}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1.35fr 1fr 1fr auto' }, gap: 0.8, alignItems: 'center', p: 1, borderRadius: '12px', bgcolor: rgba(palette.rust, .035), border: `1px solid ${rgba(palette.rust, .10)}` }}>
                                <TextField type="date" size="small" value={row.date} onChange={(e) => updateAvailabilityRow(index, 'date', e.target.value)} inputProps={{ min: new Date().toISOString().slice(0, 10) }} />
                                <TextField type="time" size="small" value={row.start} onChange={(e) => updateAvailabilityRow(index, 'start', e.target.value)} />
                                <TextField type="time" size="small" value={row.end} onChange={(e) => updateAvailabilityRow(index, 'end', e.target.value)} />
                                <IconButton aria-label="Remove availability window" disabled={availabilityRows.length === 1} onClick={() => setAvailabilityRows((rows) => rows.filter((_, idx) => idx !== index))} sx={{ color: palette.danger }}>
                                    <CloseIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </Box>
                        ))}
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            disabled={availabilityRows.length >= 7}
                            onClick={() => setAvailabilityRows((rows) => [...rows, newAvailabilityRow(rows.length + 1)])}
                            sx={{ alignSelf: 'flex-start', color: getReadableColor(palette.rust), borderColor: rgba(palette.rust, .22) }}
                        >
                            Add another window
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 2.25, py: 1.6, borderTop: `1px solid ${palette.hairline}` }}>
                    <Button onClick={() => setAvailabilityRequest(null)} disabled={sharingAvailability} sx={{ color: palette.inkSoft }}>Cancel</Button>
                    <GlassSolidButton
                        label={sharingAvailability ? 'Sharing…' : 'Share availability'}
                        color={palette.rust}
                        icon={sharingAvailability ? <CircularProgress size={14} color="inherit" /> : <CalendarIcon sx={{ fontSize: 14 }} />}
                        onClick={shareSelectedAvailability}
                        disabled={sharingAvailability}
                    />
                </DialogActions>
            </Dialog>
        </Box>
    );
};

/* ── Add Team Members Dialog ────────────────────────────────────────────── */

const AddTeamMembersDialog = ({ open, onClose, meeting, onAddMembers }) => {
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [loading, setLoading] = useState(false);

    const { userData, companyData, currentUser } = useAuth();
    
    const companyId = companyData?.companyId || 
                      companyData?.id || 
                      userData?.companyId || 
                      userData?.id || 
                      companyData?.uid ||
                      userData?.uid ||
                      currentUser?.uid;

    useEffect(() => {
        if (open && meeting) {
            fetchTeamMembers();
        }
    }, [open, meeting]);

    const fetchTeamMembers = async () => {
        setLoading(true);
        try {
            if (!companyId) {
                toast.error('Company Identity missing. Cannot load members.');
                setTeamMembers([]);
                setFilteredMembers([]);
                setLoading(false);
                return;
            }

            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('companyId', '==', companyId));

            const querySnapshot = await getDocs(q);
            const members = [];
            
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                if (!data.isBlocked) {
                    members.push({
                        id: doc.id,
                        name: data.displayName || data.firstName || 'Unnamed User',
                        email: data.email || '',
                        role: data.role || 'employee',
                        department: data.department || '',
                        designation: data.designation || '',
                        ...data
                    });
                }
            });

            setTeamMembers(members);
            
            const existingIds = (meeting.participants || []).map(p => p.id || p.userId);
            const availableMembers = members.filter(m => !existingIds.includes(m.id));
            setFilteredMembers(availableMembers);
            setSelectedMembers([]);
        } catch (error) {
            console.error('Error fetching team members:', error);
            toast.error('Failed to load team members');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (event) => {
        const term = event.target.value.toLowerCase();
        setSearchTerm(term);
        
        const existingIds = (meeting?.participants || []).map(p => p.id || p.userId);
        const availableMembers = teamMembers.filter(m => !existingIds.includes(m.id));
        
        const filtered = availableMembers.filter(member =>
            (member.name || '').toLowerCase().includes(term) ||
            (member.email || '').toLowerCase().includes(term) ||
            (member.designation || '').toLowerCase().includes(term) ||
            (member.department || '').toLowerCase().includes(term)
        );
        setFilteredMembers(filtered);
    };

    const handleToggleMember = (member) => {
        setSelectedMembers(prev => {
            const exists = prev.find(m => m.id === member.id);
            if (exists) {
                return prev.filter(m => m.id !== member.id);
            } else {
                return [...prev, member];
            }
        });
    };

    const handleAddMembers = async () => {
        if (selectedMembers.length === 0) {
            toast.error('Please select at least one team member');
            return;
        }

        setLoading(true);
        try {
            const memberData = selectedMembers.map(m => ({
                id: m.id,
                name: m.name || m.displayName || 'Team Member',
                email: m.email || '',
                role: m.role || 'employee',
                department: m.department || '',
                designation: m.designation || '',
                addedAt: new Date().toISOString(),
            }));

            await onAddMembers(meeting, memberData);
            onClose();
        } catch (error) {
            console.error('Error adding members:', error);
            toast.error('Failed to add members');
        } finally {
            setLoading(false);
        }
    };

    const getInitials = (member) => {
        if (member.name) {
            const parts = member.name.split(' ');
            return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
        }
        return 'U';
    };

    const existingIds = (meeting?.participants || []).map(p => p.id || p.userId);
    const availableCount = teamMembers.filter(m => !existingIds.includes(m.id)).length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        ...liquid(palette.teal, { radius: 24 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 60%, ${rgba(palette.teal, 0.06)})`,
                        maxHeight: '90vh',
                    }
                },
                backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: rgba(palette.ink, 0.28) } }
            }}
        >
            <Box
                sx={{
                    p: 2.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${palette.hairlineSoft}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <GlassIcon icon={<GroupAddIcon />} color={palette.teal} size={44} />
                    <Box>
                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.1rem', color: palette.ink }}>
                            Add Team Members
                        </Typography>
                        <Typography sx={{ color: palette.inkLight, fontSize: '0.72rem' }}>
                            Add team members to "{meeting?.title || 'Meeting'}"
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        width: 32, height: 32, borderRadius: '10px', color: palette.inkSoft,
                        bgcolor: rgba(palette.ink, 0.04),
                        '&:hover': { bgcolor: rgba(palette.ink, 0.08), transform: 'rotate(90deg)' },
                        transition: 'transform .3s ease, background .3s ease',
                    }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            <DialogContent sx={{ px: 2.25, py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, p: 1.5, ...glassWell(14) }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '10px',
                            display: 'grid',
                            placeItems: 'center',
                            color: getGradientContrastText(palette.rust, palette.rustDeep),
                            background: `linear-gradient(150deg, ${palette.rust}, ${palette.rustDeep})`,
                            boxShadow: `0 4px 12px ${rgba(palette.rust, 0.3)}`,
                        }}
                    >
                        {meeting?.title?.charAt(0) || 'M'}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.85rem' }} noWrap>
                            {meeting?.title || 'Meeting'}
                        </Typography>
                        <Typography sx={{ color: palette.inkLight, fontSize: '0.68rem' }}>
                            {meeting?.scheduledTime ? formatDateTime(meeting.scheduledTime) : 'Date TBD'} • {meeting?.duration || 30} min
                        </Typography>
                    </Box>
                    <GlassPill
                        label={`${meeting?.participants?.length || 0} participants`}
                        color={palette.teal}
                        icon={<PeopleIcon sx={{ fontSize: 12 }} />}
                    />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.82rem' }}>
                        Available Team Members ({availableCount})
                    </Typography>
                    <Button
                        size="small"
                        onClick={() => {
                            if (selectedMembers.length === filteredMembers.length) {
                                setSelectedMembers([]);
                            } else {
                                setSelectedMembers(filteredMembers);
                            }
                        }}
                        sx={{ color: palette.rust, fontWeight: 600, textTransform: 'none', fontSize: '0.72rem' }}
                    >
                        {selectedMembers.length === filteredMembers.length ? 'Deselect All' : 'Select All'}
                    </Button>
                </Box>

                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search team members..."
                    value={searchTerm}
                    onChange={handleSearch}
                    sx={{
                        mb: 2,
                        ...glassWell(12),
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            bgcolor: 'transparent',
                        }
                    }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: palette.inkLight, fontSize: 18 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                                        <ClearIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }
                    }}
                />

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={36} sx={{ color: palette.rust }} />
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                        {filteredMembers.length === 0 ? (
                            <EmptyState
                                icon={<PeopleIcon />}
                                text={teamMembers.length === 0 ? 'No team members found in your company database.' : 'All team members are already in this meeting.'}
                            />
                        ) : (
                            filteredMembers.map((member) => {
                                const isSelected = selectedMembers.find(m => m.id === member.id);
                                return (
                                    <Box
                                        key={member.id}
                                        onClick={() => handleToggleMember(member)}
                                        sx={{
                                            p: 1.25,
                                            mb: 0.75,
                                            borderRadius: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            border: `1px solid ${isSelected ? rgba(palette.rust, 0.3) : palette.hairline}`,
                                            background: isSelected ? rgba(palette.rust, 0.04) : 'transparent',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                borderColor: palette.rust,
                                                background: rgba(palette.rust, 0.04),
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                            <Avatar
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: '10px',
                                                    bgcolor: isSelected ? palette.rust : palette.hairline,
                                                    color: isSelected ? '#fff' : palette.inkLight,
                                                    fontWeight: 600,
                                                    fontFamily: fontDisplay,
                                                    fontSize: '0.85rem',
                                                }}
                                            >
                                                {getInitials(member)}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.82rem' }} noWrap>
                                                    {member.name || 'Unnamed User'}
                                                </Typography>
                                                <Typography sx={{ color: palette.inkLight, fontSize: '0.68rem' }} noWrap>
                                                    {member.email}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                            <GlassPill
                                                label={member.role || 'Employee'}
                                                color={palette.teal}
                                                size="sm"
                                            />
                                            {isSelected && (
                                                <CheckCircleIcon sx={{ color: palette.rust, fontSize: 18 }} />
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                )}

                {selectedMembers.length > 0 && (
                    <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', ...glassWell(12) }}>
                        <Typography sx={{ color: palette.pine, fontWeight: 600, fontSize: '0.72rem' }}>
                            {selectedMembers.length} member{selectedMembers.length !== 1 ? 's' : ''} selected
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {selectedMembers.map((member) => (
                                <GlassPill
                                    key={member.id}
                                    label={member.name}
                                    color={palette.rust}
                                    size="sm"
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <Box
                sx={{
                    px: 2.25,
                    py: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1,
                    borderTop: `1px solid ${palette.hairlineSoft}`,
                    background: rgba('#FFFFFF', 0.45),
                    backdropFilter: 'blur(8px)',
                }}
            >
                <Button
                    onClick={onClose}
                    sx={{ textTransform: 'none', fontWeight: 600, color: palette.inkSoft, borderRadius: '10px', px: 1.75, fontSize: '0.78rem' }}
                >
                    Cancel
                </Button>
                <GlassSolidButton
                    label={`Add ${selectedMembers.length} Member${selectedMembers.length !== 1 ? 's' : ''}`}
                    color={palette.rust}
                    icon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <GroupAddIcon sx={{ fontSize: 14 }} />}
                    onClick={handleAddMembers}
                    disabled={selectedMembers.length === 0 || loading}
                />
            </Box>
        </Dialog>
    );
};

/* ── Meeting Card ──────────────────────────────────────────────────────────── */

const MeetingCard = ({ meeting, viewMode = 'grid', onView, onCancel, onJoin, onApprove, onReject, onManageTeam, type, companyDirectory = {}, userDirectory = {}, currentCompanyId = '', currentUserId = '' }) => {
    const isList = viewMode === 'list';
    
    const normalizedStatus = normalizeMeetingStatus(meeting.status);
    const isUpcoming = ['scheduled', 'confirmed', 'accepted'].includes(normalizedStatus);
    const isPast = ['completed', 'cancelled', 'declined', 'rejected'].includes(normalizedStatus);
    const isPending = normalizedStatus === 'pending';
    const isInProgress = normalizedStatus === 'in_progress';
    const isReceived = type === 'received';
    const isSent = type === 'sent';
    const isConfirmed = normalizedStatus === 'confirmed' || normalizedStatus === 'accepted';

    const counterpartCompanyId = String(isReceived ? (meeting.requesterId || meeting.requesterCompanyId || '') : (meeting.targetId || meeting.targetCompanyId || ''));
    const alternateCompanyId = String(isReceived ? (meeting.targetId || meeting.targetCompanyId || '') : (meeting.requesterId || meeting.requesterCompanyId || ''));
    const resolvedCompanyId = counterpartCompanyId && counterpartCompanyId !== String(currentCompanyId || '') ? counterpartCompanyId : alternateCompanyId;
    const counterpartUserId = String(isReceived ? (meeting.requesterUserId || '') : (meeting.targetUserId || ''));
    const counterpartCompanyName = String(isReceived ? (meeting.requesterCompany || meeting.requesterCompanyName || '') : (meeting.targetCompany || meeting.targetCompanyName || ''));
    const participantPerson = (meeting.participants || []).find((p) => p && typeof p === 'object' && String(p.userId || p.id || '') !== String(currentUserId || '')) || {};
    const participantUserId = String(participantPerson.userId || participantPerson.id || '');
    const participantName = String(participantPerson.name || participantPerson.displayName || participantPerson.userName || '');
    const companyEntity = companyDirectory[resolvedCompanyId] || companyDirectory[`name:${counterpartCompanyName.trim().toLowerCase()}`] || {};
    const userEntity = userDirectory[counterpartUserId] || userDirectory[participantUserId] || userDirectory[`name:${participantName.trim().toLowerCase()}`] || {};
    const userPhoto = userEntity.photoURL || userEntity.profileImage || userEntity.profilePicture || userEntity.avatarUrl || participantPerson.photoURL || participantPerson.profileImage || participantPerson.profilePicture || participantPerson.avatarUrl || meeting.targetUserPhoto || meeting.requesterPhoto || meeting.targetUserPhotoURL || meeting.requesterUserPhotoURL || meeting.targetPhotoURL || meeting.requesterPhotoURL || meeting.userPhotoURL || '';
    const hasCompanyEntity = Boolean(companyEntity && Object.keys(companyEntity).length);
    const companyLogo = hasCompanyEntity ? getCompanyLogoUrl(companyEntity) : (meeting.companyLogo || meeting.companyLogoUrl || meeting.targetCompanyLogo || meeting.targetCompanyLogoUrl || meeting.requesterCompanyLogo || meeting.requesterCompanyLogoUrl || '');
    const companyCoverCandidate = hasCompanyEntity ? getCompanyCoverUrl(companyEntity) : (meeting.companyCoverImage || meeting.targetCompanyCover || meeting.targetCompanyCoverImage || meeting.requesterCompanyCover || meeting.requesterCompanyCoverImage || '');
    // Never stretch a company/platform logo into the cover surface. Old meeting
    // rows sometimes stored a logo in the generic coverImage field.
    const companyCover = companyCoverCandidate && companyCoverCandidate !== companyLogo ? companyCoverCandidate : '';
    const counterpartPersonName = participantName || String(isReceived ? (meeting.requesterName || meeting.requesterUserName || '') : (meeting.targetName || meeting.targetUserName || '')) || counterpartCompanyName || 'User';
    const personInitials = counterpartPersonName.split(/\s+/).slice(0, 2).map((part) => part?.[0] || '').join('').toUpperCase() || 'U';
    const visualAvatar = userPhoto || '';

    const [timeUntilStart, setTimeUntilStart] = useState('');
    const statusColor = toneFor(meeting.status);

    const canStartMeeting = useCallback(() => {
        if (!meeting.scheduledTime) return false;
        const scheduledTime = toDate(meeting.scheduledTime);
        if (!scheduledTime) return false;
        const now = new Date();
        return now >= scheduledTime;
    }, [meeting.scheduledTime]);

    useEffect(() => {
        if (!meeting.scheduledTime || isPast || isInProgress) return;

        const updateTimer = () => {
            const scheduledTime = toDate(meeting.scheduledTime);
            if (!scheduledTime) return;
            const now = new Date();
            const diff = scheduledTime - now;

            if (diff <= 0) {
                setTimeUntilStart('Ready to join!');
                return;
            }

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            if (days > 0) {
                setTimeUntilStart(`${days}d ${hours}h ${minutes}m`);
            } else if (hours > 0) {
                setTimeUntilStart(`${hours}h ${minutes}m ${seconds}s`);
            } else if (minutes > 0) {
                setTimeUntilStart(`${minutes}m ${seconds}s`);
            } else {
                setTimeUntilStart(`${seconds}s`);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [meeting.scheduledTime, isPast, isInProgress]);

    const isReady = canStartMeeting() || timeUntilStart === 'Ready to join!';

    const getActionButtons = () => {
        if (isReceived && isPending) {
            return (
                <>
                    <GlassSolidButton
                        label="Approve"
                        color={palette.pine}
                        icon={<ThumbUpIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => { e.stopPropagation(); onApprove(meeting); }}
                        size="small"
                    />
                    <GlassSolidButton
                        label="Reject"
                        color={palette.danger}
                        variant="soft"
                        icon={<ThumbDownIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => { e.stopPropagation(); onReject(meeting); }}
                        size="small"
                    />
                </>
            );
        }

        if (isSent && isPending) {
            return (
                <>
                    <GlassPill
                        label="Awaiting Response"
                        color={palette.amber}
                        icon={<PendingIcon sx={{ fontSize: 12 }} />}
                    />
                    <GlassSolidButton
                        label="Add Members"
                        color={palette.teal}
                        variant="soft"
                        icon={<GroupAddIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => { e.stopPropagation(); onManageTeam(meeting); }}
                        size="small"
                    />
                </>
            );
        }

        if (isUpcoming) {
            return (
                <>
                    <GlassSolidButton
                        label={isReady ? 'Join Now' : `Wait`}
                        color={isReady ? palette.rust : palette.inkLight}
                        icon={isReady ? <VideoCallIcon sx={{ fontSize: 14 }} /> : <TimerIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => { 
                            e.stopPropagation(); 
                            if (isReady) onJoin(meeting);
                            else toast.info(`Meeting starts in ${timeUntilStart}`);
                        }}
                        size="small"
                    />
                    <GlassSolidButton
                        label="Add Members"
                        color={palette.teal}
                        variant="soft"
                        icon={<GroupAddIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => { e.stopPropagation(); onManageTeam(meeting); }}
                        size="small"
                    />
                </>
            );
        }

        if (isInProgress) {
            return (
                <GlassSolidButton
                    label="Join Now"
                    color={palette.rust}
                    icon={<VideoCallIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); onJoin(meeting); }}
                    size="small"
                />
            );
        }

        if (isPast) {
            return (
                <GlassSolidButton
                    label="View Summary"
                    color={palette.inkLight}
                    variant="soft"
                    icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); onView(meeting); }}
                    size="small"
                />
            );
        }

        return null;
    };

    const participantCount = meeting.participants?.length || 0;

    const detailChips = [
        { label: meeting.type || 'Video Call', icon: <VideoCallIcon sx={{ fontSize: 12 }} />, color: palette.rust },
        { label: `${meeting.duration || 30} min`, icon: <AccessTimeIcon sx={{ fontSize: 12 }} />, color: palette.teal },
        { label: `${participantCount} participants`, icon: <PeopleIcon sx={{ fontSize: 12 }} />, color: palette.pine },
    ];

    const showCountdown = isUpcoming && !isReady && timeUntilStart;

    return (
        <MotionDiv
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
            <Paper
                elevation={0}
                className="tf-meeting-card"
                onClick={() => onView(meeting)}
                sx={{
                    '--meeting-status-color': statusColor,
                    ...liquid(statusColor, { radius: 20, depth: 0.7 }),
                    p: 1.35,
                    pt: companyCover ? 6.8 : 1.35,
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    '&:hover': {
                        transform: 'translateY(-3px)',
                    },
                }}
            >
                {companyCover && <Box aria-hidden="true" className="tf-fixed-cover tf-meeting-cover" sx={{ position:'absolute', top:0, left:0, right:0, height:58, zIndex:0, backgroundImage:`linear-gradient(180deg,rgba(7,12,18,.05),rgba(7,12,18,.22)), url("${companyCover}")`, backgroundSize:'cover', backgroundPosition:'center', opacity:.96, pointerEvents:'none' }} />}
                {!companyCover && <Box aria-hidden="true" className="tf-meeting-cover-fallback" sx={{ position:'absolute', top:0, left:0, right:0, height:6, zIndex:0, background:`linear-gradient(90deg,${rgba(statusColor,.55)},${rgba(statusColor,.12)},transparent)`, pointerEvents:'none' }} />}
                <Box className="tf-meeting-card-watermark">{isReceived ? <InboxIcon /> : <VideoCallIcon />}</Box>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 2,
                        background: `linear-gradient(90deg, ${statusColor}, ${statusColor}60)`,
                        opacity: isReceived && isPending ? 1 : 0.4,
                    }}
                />

                <Box sx={{
                    display: 'flex',
                    flexDirection: isList ? { xs: 'column', md: 'row' } : 'column',
                    gap: isList ? 1.5 : 1,
                    alignItems: isList ? { xs: 'stretch', md: 'center' } : 'stretch',
                }}>
                    
                    {/* LEFT COLUMN: Icon, Title, and Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                <Box className="tf-meeting-identity-stack" sx={{ position: 'relative', width: 54, height: 58, flexShrink: 0, display:'flex', justifyContent:'center' }}>
                                    <Avatar
                                        src={visualAvatar || undefined}
                                        sx={{
                                            width: 48, height: 48, borderRadius: '50%',
                                            color: getContrastText(statusColor), fontWeight: 900, fontSize: '.78rem',
                                            background: `linear-gradient(150deg, ${rgba(statusColor, 0.94)}, ${rgba(statusColor, 0.64)})`,
                                            border: '3px solid rgba(255,255,255,.96)',
                                            boxShadow: `0 8px 20px ${rgba(statusColor, 0.26)}`,
                                        }}
                                    >
                                        {!visualAvatar && personInitials}
                                    </Avatar>
                                    {companyLogo && <Avatar src={companyLogo} sx={{ position: 'absolute', left:'50%', transform:'translateX(-50%)', bottom: 0, width: 22, height: 22, borderRadius: '50%', bgcolor: '#fff', border: '2px solid #fff', boxShadow: '0 4px 10px rgba(25,57,78,.18)', '& img': { objectFit: 'contain', padding:'2px' } }} />}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.92rem' }} noWrap>
                                            {meeting.title || 'Meeting'}
                                        </Typography>
                                        {isReceived && isPending && (
                                            <GlassPill label="Request" color={palette.amber} size="sm" filled />
                                        )}
                                        {meeting.isAI && (
                                            <GlassPill label="AI" color={palette.plum} size="sm" icon={<AiIcon sx={{ fontSize: 10 }} />} />
                                        )}
                                        {isConfirmed && (
                                            <GlassPill label="Confirmed" color={palette.pine} size="sm" icon={<CheckCircleIcon sx={{ fontSize: 10 }} />} />
                                        )}
                                        {showCountdown && (
                                            <GlassPill
                                                label={timeUntilStart}
                                                color={palette.amber}
                                                size="sm"
                                                icon={<TimerIcon sx={{ fontSize: 10 }} />}
                                                filled
                                            />
                                        )}
                                    </Box>
                                    <Typography sx={{ color: palette.inkSoft, fontSize: '0.68rem', fontWeight:700 }} noWrap>
                                        {counterpartPersonName}
                                    </Typography>
                                    <Typography sx={{ color: palette.inkLight, fontSize: '0.62rem' }} noWrap>
                                        {isReceived ? meeting.requesterCompany || counterpartCompanyName || 'Unknown Company' : meeting.targetCompany || counterpartCompanyName || 'Unknown Company'}
                                        {isReceived && ' · requested you'}
                                    </Typography>
                                </Box>
                            </Box>
                            {!isList && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                    <MeetingStatusChip status={meeting.status} />
                                </Box>
                            )}
                        </Box>

                        {/* Detail Chips */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: isList ? 0 : 0.9 }}>
                            {detailChips.map((chip, index) => (
                                <Chip
                                    key={index}
                                    icon={chip.icon}
                                    label={chip.label}
                                    size="small"
                                    sx={{
                                        bgcolor: rgba(chip.color, 0.06),
                                        color: getReadableColor(chip.color),
                                        fontWeight: 700,
                                        fontSize: '0.65rem',
                                        height: 22,
                                        border: `1px solid ${rgba(chip.color, 0.1)}`,
                                        '& .MuiChip-icon': { color: getReadableColor(chip.color), fontSize: 13 },
                                    }}
                                />
                            ))}
                        </Box>

                        {!isList && meeting.agenda && (
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem', mb: 1.5, lineHeight: 1.5 }}>
                                {meeting.agenda.length > 100 ? `${meeting.agenda.substring(0, 100)}...` : meeting.agenda}
                            </Typography>
                        )}
                        
                        {/* Show message preview if exists */}
                        {meeting.message && (
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'flex-start', 
                                gap: 0.75, 
                                mt: 0.5,
                                p: 1,
                                borderRadius: '8px',
                                bgcolor: rgba(palette.ink, 0.03),
                                border: `1px solid ${palette.hairline}`,
                            }}>
                                <MessageIcon sx={{ fontSize: 14, color: palette.inkFaint, mt: 0.25 }} />
                                <Typography sx={{ 
                                    color: palette.inkSoft, 
                                    fontSize: '0.7rem', 
                                    lineHeight: 1.4,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}>
                                    {meeting.message}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* RIGHT COLUMN: Actions & Optional Status (List View) */}
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: isList ? 'row' : 'row', 
                        gap: 1.5, 
                        flexShrink: 0,
                        alignItems: 'center',
                        justifyContent: isList ? 'flex-end' : 'flex-start',
                        flexWrap: 'wrap'
                    }}>
                        {isList && (
                            <Box sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
                                <MeetingStatusChip status={meeting.status} />
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            {getActionButtons()}
                        </Box>
                    </Box>

                </Box>
            </Paper>
        </MotionDiv>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   5 · MEETING DASHBOARD
   ═══════════════════════════════════════════════════════════════════════════ */


const MeetingCommandCards = ({ navigate, agoraConfigured }) => {
    const cards = [
        {
            color: '#2F6FED', icon: <CalendarIcon />, title: 'Meeting Requests', badge: 'RECIPIENT CONTROLLED',
            text: 'Introductions and recipient permissions control the booking flow. Schedule only with people who explicitly shared their availability.',
            chips: ['Introductions', 'Permissions', 'Approvals'],
            action: 'Schedule Meeting', onClick: () => navigate('/dashboard/meetings/schedule'), metric: 'CONTROLLED', metricLabel: 'Booking workflow', watermark: <CalendarIcon />,
        },
        {
            color: '#8B4DE8', icon: <ScheduleIcon />, title: 'Shared Availability', badge: 'SETTINGS MANAGED',
            text: 'Working hours, slot duration and connected-calendar conflict checks are configured centrally in Settings and remain private until shared.',
            chips: ['Working Hours', 'Slot Duration', 'Privacy'],
            action: 'Calendar Settings', onClick: () => navigate('/dashboard/company/settings?tab=calendar'), metric: 'PRIVATE', metricLabel: 'Default access', watermark: <AccessTimeIcon />,
        },
        {
            color: '#13A773', icon: <VideoCallIcon />, title: 'TELCOFAIR Video Room', badge: 'AGORA RTC',
            text: agoraConfigured ? 'Secure in-platform video rooms are ready for confirmed meetings with camera, microphone and participant controls.' : 'Video meetings remain visible, while live-room access becomes available after the platform video service is configured in Settings.',
            chips: ['Secure Token', 'Camera & Mic', 'In-app Meeting'],
            action: 'View Meetings', onClick: () => window.scrollTo({ top: 760, behavior: 'smooth' }), metric: agoraConfigured ? 'READY' : 'SETUP', metricLabel: 'Video service', watermark: <MeetingRoomIcon />,
        },
    ];

    return <Box className="tf-meeting-command-grid">
        {cards.map((card) => <Paper key={card.title} elevation={0} className="tf-meeting-command-card" sx={{ '--meeting-card-color': card.color }}>
            <Box className="tf-meeting-command-watermark">{card.watermark}</Box>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, position: 'relative', zIndex: 2 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><Box className="tf-meeting-command-icon">{card.icon}</Box><Box><Typography sx={{ fontSize: 12.8, fontWeight: 950, color: '#1D2B3E' }}>{card.title}</Typography><Typography sx={{ mt: .15, fontSize: 8.2, letterSpacing: '.06em', fontWeight: 900, color: card.color }}>{card.badge}</Typography></Box></Stack>
                <Box sx={{ textAlign: 'right' }}><Typography sx={{ fontSize: 13, fontWeight: 950, color: card.color }}>{card.metric}</Typography><Typography sx={{ fontSize: 8.1, color: '#8B98A8', fontWeight: 750 }}>{card.metricLabel}</Typography></Box>
            </Stack>
            <Typography sx={{ position: 'relative', zIndex: 2, mt: .75, minHeight: 28, fontSize: 10.3, lineHeight: 1.52, color: '#67778B', fontWeight: 600 }}>{card.text}</Typography>
            <Stack direction="row" spacing={.45} useFlexGap sx={{ flexWrap: 'wrap', position: 'relative', zIndex: 2, mt: .75 }}>{card.chips.map((x) => <Chip key={x} size="small" label={x} sx={{ height: 21, fontSize: 8.3, bgcolor: `${card.color}0B`, color: card.color, border: `1px solid ${card.color}14` }} />)}</Stack>
            <Button onClick={card.onClick} endIcon={<SendIcon />} sx={{ position: 'relative', zIndex: 2, mt: 1.05, minHeight: 30, px: 1.1, fontSize: 9.1, fontWeight: 900, color: '#fff !important', background: `linear-gradient(135deg,${card.color},color-mix(in srgb,${card.color} 68%,#fff)) !important`, boxShadow: `0 7px 16px ${rgba(card.color,.2)} !important` }}>{card.action}</Button>
        </Paper>)}
    </Box>;
};

const MeetingDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userData, companyData, currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [meetings, setMeetings] = useState([]);
    const [filteredMeetings, setFilteredMeetings] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const [viewMode, setViewMode] = useState('grid');
    const [searchTerm, setSearchTerm] = useState('');
    const [aiRequests, setAiRequests] = useState([]);
    const [sentRequests, setSentRequests] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        upcoming: 0,
        pending: 0,
        completed: 0,
        inProgress: 0,
        received: 0,
        aiRequests: 0,
    });
    const [selectedMeeting, setSelectedMeeting] = useState(null);
    const [openSummaryDialog, setOpenSummaryDialog] = useState(false);
    const [openCancelDialog, setOpenCancelDialog] = useState(false);
    const [openAddTeamDialog, setOpenAddTeamDialog] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [unsubscribeSent, setUnsubscribeSent] = useState(null);
    const [unsubscribeReceived, setUnsubscribeReceived] = useState(null);
    const [unsubscribeAiRequests, setUnsubscribeAiRequests] = useState(null);
    const [agoraConfig, setAgoraConfig] = useState(null);
    const [companyDirectory, setCompanyDirectory] = useState({});
    const [userDirectory, setUserDirectory] = useState({});

    const getCompanyId = useCallback(() => {
        const id = companyData?.companyId || 
                  companyData?.id || 
                  userData?.companyId || 
                  userData?.id || 
                  companyData?.uid ||
                  userData?.uid ||
                  currentUser?.uid;
        
        if (!id) {
            console.warn('No company ID found in auth context');
            return null;
        }
        return id;
    }, [companyData, userData, currentUser]);

    const getCompanyName = useCallback(() => {
        return companyData?.companyName || 
               companyData?.name || 
               userData?.companyName || 
               userData?.name || 
               'Your Company';
    }, [companyData, userData]);

    useEffect(() => {
        let active = true;
        Promise.allSettled([getDocs(collection(db, 'companies')), getDocs(collection(db, 'users'))]).then(([companiesResult, usersResult]) => {
            if (!active) return;
            if (companiesResult.status === 'fulfilled') {
                const map = {};
                companiesResult.value.docs.forEach((snap) => {
                    const row = { id: snap.id, ...snap.data() };
                    map[snap.id] = row;
                    if (row.companyId) map[row.companyId] = row;
                    const nameKey = String(row.companyName || row.name || '').trim().toLowerCase();
                    if (nameKey) map[`name:${nameKey}`] = row;
                });
                setCompanyDirectory(map);
            }
            if (usersResult.status === 'fulfilled') {
                const map = {};
                usersResult.value.docs.forEach((snap) => {
                    const row = { id: snap.id, ...snap.data() };
                    map[snap.id] = row;
                    if (row.uid) map[row.uid] = row;
                    const nameKey = String(row.displayName || row.name || [row.firstName, row.lastName].filter(Boolean).join(' ')).trim().toLowerCase();
                    if (nameKey) map[`name:${nameKey}`] = row;
                });
                setUserDirectory(map);
            }
        }).catch(() => undefined);
        return () => { active = false; };
    }, []);

    // ── Fetch Agora Configuration ──────────────────────────────────────────

    const fetchAgoraConfig = useCallback(async () => {
        try {
            // Only public Agora metadata belongs in the browser. The App
            // Certificate and RTC-token signing credentials must remain in the
            // backend function used by VideoCall/fetchAgoraToken.
            const runtimeDoc = await getDoc(doc(db, 'systemSettings', 'public_runtime'));
            const runtime = runtimeDoc.exists() ? runtimeDoc.data() || {} : {};
            const config = {
                enabled: Boolean(runtime.meeting?.agoraEnabled),
                appId: runtime.meeting?.agoraAppId || process.env.REACT_APP_AGORA_APP_ID || '',
            };
            setAgoraConfig(config);
            return config;
        } catch (error) {
            console.error('Error fetching public Agora config:', error);
            const config = {
                enabled: Boolean(process.env.REACT_APP_AGORA_APP_ID),
                appId: process.env.REACT_APP_AGORA_APP_ID || '',
            };
            setAgoraConfig(config);
            return config;
        }
    }, []);

    // ── Join Meeting with Agora ────────────────────────────────────────────

    const handleJoinMeeting = useCallback(async (meeting) => {
        if (!meeting.scheduledTime) {
            toast.error('This meeting has no scheduled time.');
            return;
        }
        const scheduledTime = toDate(meeting.scheduledTime);
        if (!scheduledTime) {
            toast.error('Invalid scheduled time.');
            return;
        }
        const now = new Date();
        if (now < scheduledTime) {
            const diff = scheduledTime - now;
            const minutes = Math.floor(diff / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            toast.info(`Meeting starts in ${minutes}m ${seconds}s`);
            return;
        }

        try {
            // Fetch Agora config
            const config = await fetchAgoraConfig();
            
            if (!config.appId) {
                toast.error('Agora App ID not configured. Please contact administrator.');
                return;
            }

            // Generate a unique channel name using meeting ID
            const channelName = `meeting_${meeting.id}`;
            
            // Never generate or transport an Agora token from a browser-side
            // certificate. The room component obtains a short-lived token from the
            // authenticated getAgoraRtcToken backend callable.
            const token = '';

            // Navigate to meeting room with all necessary data
            navigate(`/dashboard/meetings/room/${meeting.id}`, {
                state: {
                    meetingId: meeting.id,
                    meetingTitle: meeting.title,
                    channelName: channelName,
                    appId: config.appId,
                    token: token,
                    uid: currentUser?.uid || 'user_' + Date.now(),
                    userName: userData?.displayName || userData?.firstName || 'User',
                    meetingData: meeting,
                }
            });

            toast.success('Joining meeting...');
        } catch (error) {
            console.error('Error joining meeting:', error);
            toast.error('Failed to join meeting. Please try again.');
        }
    }, [fetchAgoraConfig, navigate, currentUser, userData]);

    // ── Fetch AI Requests ──────────────────────────────────────────────────

    const fetchAIRequests = useCallback(async () => {
        const companyId = getCompanyId();
        if (!companyId || !currentUser?.uid) return;
        try {
            const normalizeIntro = (snap, source, direction) => {
                const data = snap.data() || {};
                return {
                    id: snap.id,
                    ...data,
                    _sourceCollection: source,
                    _direction: direction,
                    senderUserId: data.senderUserId || data.fromUserId,
                    senderName: data.senderName || data.fromUserName,
                    senderCompany: data.senderCompany || data.fromCompanyName,
                    senderPhotoURL: data.senderPhotoURL || '',
                    targetUserId: data.targetUserId || data.toUserId,
                    targetName: data.targetName || data.toUserName,
                    targetCompany: data.targetCompany || data.toCompanyName,
                    message: data.message || data.aiMessage || '',
                    chatEnabled: Boolean(data.chatEnabled || data.recipientDecision === 'chat' || data.recipientDecision === 'both'),
                    calendarShared: Boolean(data.calendarShared || data.recipientDecision === 'calendar' || data.recipientDecision === 'both'),
                };
            };

            const [legacyIncoming, legacyOutgoing, canonicalIncoming, canonicalOutgoing] = await Promise.all([
                getDocs(query(collection(db, 'ai_introductions'), where('targetUserId', '==', currentUser.uid))),
                getDocs(query(collection(db, 'ai_introductions'), where('senderUserId', '==', currentUser.uid))),
                getDocs(query(collection(db, 'introductions'), where('toUserId', '==', currentUser.uid))),
                getDocs(query(collection(db, 'introductions'), where('fromUserId', '==', currentUser.uid))),
            ]);
            const incomingRequests = [
                ...legacyIncoming.docs.map(d => normalizeIntro(d, 'ai_introductions', 'received')),
                ...canonicalIncoming.docs.map(d => normalizeIntro(d, 'introductions', 'received')),
            ];
            const outgoingRequests = [
                ...legacyOutgoing.docs.map(d => normalizeIntro(d, 'ai_introductions', 'sent')),
                ...canonicalOutgoing.docs.map(d => normalizeIntro(d, 'introductions', 'sent')),
            ];
            const byKey = new Map();
            [...incomingRequests, ...outgoingRequests].forEach((item) => {
                const key = `${item.senderUserId || ''}_${item.targetUserId || ''}_${item.id}`;
                byKey.set(key, item);
            });
            const all = [...byKey.values()].sort((a, b) => {
                const ms = (v) => v?.toMillis?.() ?? (v?.seconds ? v.seconds * 1000 : new Date(v || 0).getTime() || 0);
                return ms(b.createdAt) - ms(a.createdAt);
            });
            setAiRequests(all);
            setSentRequests(outgoingRequests);
            setStats(prev => ({ ...prev, aiRequests: incomingRequests.filter(r => r.status === 'pending').length }));
        } catch (error) {
            console.error('Error fetching AI requests:', error);
        }
    }, [getCompanyId, currentUser]);

    // ── Fetch Meetings ─────────────────────────────────────────────────────

    const fetchMeetings = useCallback(async () => {
        const companyId = getCompanyId();
        if (!companyId) {
            setLoading(false);
            setError('No company ID found. Please ensure you are logged in.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('🔍 Fetching meetings for company:', companyId);

            // Fetch sent meetings
            const sentQuery = query(
                collection(db, 'meetings'),
                where('requesterId', '==', companyId)
            );
            const sentSnapshot = await getDocs(sentQuery);
            const sentList = [];
            sentSnapshot.forEach((doc) => {
                sentList.push({ 
                    id: doc.id, 
                    ...doc.data(),
                    _type: 'sent' 
                });
            });

            // Fetch received meetings
            const receivedQuery = query(
                collection(db, 'meetings'),
                where('targetId', '==', companyId)
            );
            const receivedSnapshot = await getDocs(receivedQuery);
            const receivedList = [];
            receivedSnapshot.forEach((doc) => {
                receivedList.push({ 
                    id: doc.id, 
                    ...doc.data(),
                    _type: 'received' 
                });
            });

            // Also fetch meetings where company is a participant (for both sent and received)
            const participantQuery = query(
                collection(db, 'meetings'),
                where('participantIds', 'array-contains', companyId)
            );
            const participantSnapshot = await getDocs(participantQuery);
            const participantList = [];
            participantSnapshot.forEach((doc) => {
                const data = doc.data();
                // Skip if already in sent or received
                if (!sentList.some(m => m.id === doc.id) && 
                    !receivedList.some(m => m.id === doc.id)) {
                    participantList.push({ 
                        id: doc.id, 
                        ...data,
                        _type: 'participant' 
                    });
                }
            });

            // Legacy compatibility: older meeting creators stored only companyId.
            const legacyQuery = query(
                collection(db, 'meetings'),
                where('companyId', '==', companyId)
            );
            const legacySnapshot = await getDocs(legacyQuery);
            const legacyList = [];
            legacySnapshot.forEach((doc) => {
                if (!sentList.some(m => m.id === doc.id) &&
                    !receivedList.some(m => m.id === doc.id) &&
                    !participantList.some(m => m.id === doc.id)) {
                    legacyList.push({ id: doc.id, ...doc.data(), _type: 'legacy' });
                }
            });

            // Combine canonical and legacy meetings without dropping existing data.
            const allMeetings = [...sentList, ...receivedList, ...participantList, ...legacyList];
            
            // Deduplicate by ID
            const uniqueMeetings = allMeetings.filter((meeting, index, self) => 
                index === self.findIndex(m => m.id === meeting.id)
            );

            const meetingsWithType = uniqueMeetings.map(meeting => ({
                ...meeting,
                type: meeting._type || 
                       (meeting.requesterId === companyId ? 'sent' : 
                        meeting.targetId === companyId ? 'received' : 'participant'),
                status: meeting.status || 'pending',
                participants: meeting.participants || [],
                duration: meeting.duration || 30,
                title: meeting.title || 'Untitled Meeting',
                message: meeting.message || '',
                remarks: meeting.remarks || '',
                participantIds: meeting.participantIds || [],
            }));

            // Sort by createdAt (newest first)
            meetingsWithType.sort((a, b) => {
                const dateA = toDate(a.createdAt) || new Date(0);
                const dateB = toDate(b.createdAt) || new Date(0);
                return dateB - dateA;
            });

            setMeetings(meetingsWithType);

            // Update stats
            const newStats = {
                total: meetingsWithType.length,
                upcoming: meetingsWithType.filter(m => ['scheduled', 'confirmed', 'accepted'].includes(normalizeMeetingStatus(m.status))).length,
                pending: meetingsWithType.filter(m => normalizeMeetingStatus(m.status) === 'pending').length,
                completed: meetingsWithType.filter(m => normalizeMeetingStatus(m.status) === 'completed').length,
                inProgress: meetingsWithType.filter(m => normalizeMeetingStatus(m.status) === 'in_progress').length,
                received: meetingsWithType.filter(m => m.type === 'received' && normalizeMeetingStatus(m.status) === 'pending').length,
                aiRequests: stats.aiRequests || 0,
            };
            setStats(newStats);

            // Apply filters
            filterMeetings(meetingsWithType, tabValue, searchTerm);

        } catch (error) {
            console.error('❌ Error fetching meetings:', error);
            setError(friendlyError(error, 'Failed to load meetings'));
            toast.error('Failed to load meetings: ' + (friendlyError(error, 'Unknown error')));
        } finally {
            setLoading(false);
        }
    }, [getCompanyId, tabValue, searchTerm, stats.aiRequests]);

    const filterMeetings = useCallback((meetingsList, tab, search) => {
        // IMPORTANT: tab indexes are the actual indexes in the visible Tabs array:
        // 0 Company Users, 1 AI Introductions, 2 All, 3 Upcoming, 4 Pending,
        // 5 Completed, 6 In Progress, 7 Requests.
        // Do not derive these by subtracting one; AI Introductions is not a meeting filter.
        let filtered = [];
        switch (tab) {
            case 2: // All
                filtered = meetingsList;
                break;
            case 3: // Upcoming
                filtered = meetingsList.filter(m => ['scheduled', 'confirmed', 'accepted'].includes(normalizeMeetingStatus(m.status)));
                break;
            case 4: // Pending
                filtered = meetingsList.filter(m => normalizeMeetingStatus(m.status) === 'pending');
                break;
            case 5: // Completed
                filtered = meetingsList.filter(m => normalizeMeetingStatus(m.status) === 'completed');
                break;
            case 6: // In Progress
                filtered = meetingsList.filter(m => normalizeMeetingStatus(m.status) === 'in_progress');
                break;
            case 7: // Requests (received pending)
                filtered = meetingsList.filter(m => m.type === 'received' && normalizeMeetingStatus(m.status) === 'pending');
                break;
            default:
                filtered = meetingsList;
        }

        if (search?.trim()) {
            const term = search.toLowerCase().trim();
            filtered = filtered.filter(m => 
                (m.title || '').toLowerCase().includes(term) ||
                (m.agenda || '').toLowerCase().includes(term) ||
                (m.requesterCompany || '').toLowerCase().includes(term) ||
                (m.targetCompany || '').toLowerCase().includes(term) ||
                (m.status || '').toLowerCase().includes(term) ||
                (m.message || '').toLowerCase().includes(term) ||
                (m.remarks || '').toLowerCase().includes(term)
            );
        }

        setFilteredMeetings(filtered);
    }, []);

    const handleTabChange = useCallback((event, newValue) => {
        setTabValue(newValue);
        if (newValue === 0) {
            // Company Users tab - no filtering needed
            return;
        }
        // For meeting tabs, use the meetings data
        if (meetings.length > 0) {
            filterMeetings(meetings, newValue, searchTerm);
        }
    }, [meetings, searchTerm, filterMeetings]);

    const handleSearch = useCallback((event) => {
        const term = event.target.value;
        setSearchTerm(term);
        if (tabValue === 0) return;
        if (meetings.length > 0) {
            filterMeetings(meetings, tabValue, term);
        }
    }, [meetings, tabValue, filterMeetings]);

    const clearSearch = useCallback(() => {
        setSearchTerm('');
        if (tabValue === 0) return;
        if (meetings.length > 0) {
            filterMeetings(meetings, tabValue, '');
        }
    }, [meetings, tabValue, filterMeetings]);

    // ── AI Request Handlers ────────────────────────────────────────────────

    const handleSendAIIntro = useCallback(async (targetUser, message) => {
        const companyId = getCompanyId();
        const targetUserId = targetUser?.uid || targetUser?.userId || targetUser?.id;
        if (!companyId || !targetUserId) return;
        setProcessing(true);
        try {
            await introductionService.send(companyId, {
                targetUserId,
                message,
                context: { source: 'meeting_dashboard' },
                tone: 'professional',
            });
            toast.success(`Introduction sent to ${targetUser.name || targetUser.displayName || 'user'}. The recipient controls chat and calendar access.`);
            await fetchAIRequests();
        } catch (error) {
            console.error('Error sending AI intro:', error);
            toast.error(friendlyError(error, 'Failed to send introduction'));
        } finally {
            setProcessing(false);
        }
    }, [getCompanyId, fetchAIRequests]);

    const handleAcceptAIIntro = useCallback(async (request, action = 'chat', extra = {}) => {
        const companyId = getCompanyId();
        if (!companyId || !request?.id) return false;
        setProcessing(true);
        try {
            await introductionService.respond(companyId, request.id, action, extra);
            const count = Array.isArray(extra.availability) ? extra.availability.length : 0;
            toast.success(action === 'calendar' ? `Shared ${count || 'your'} meeting availability with the sender.` : action === 'both' ? 'Chat and calendar access enabled.' : 'Chat enabled with the sender.');
            await fetchAIRequests();
            return true;
        } catch (error) {
            console.error('Error responding to introduction:', error);
            toast.error(friendlyError(error, 'Failed to update introduction'));
            return false;
        } finally { setProcessing(false); }
    }, [getCompanyId, fetchAIRequests]);

    const handleRejectAIIntro = useCallback(async (request) => {
        const companyId = getCompanyId();
        if (!companyId || !request?.id) return;
        setProcessing(true);
        try {
            await introductionService.respond(companyId, request.id, 'ignore');
            toast('Introduction ignored.', { icon: '👋' });
            await fetchAIRequests();
        } catch (error) {
            console.error('Error ignoring introduction:', error);
            toast.error(friendlyError(error, 'Failed to ignore introduction'));
        } finally { setProcessing(false); }
    }, [getCompanyId, fetchAIRequests]);

    const handleChatFromAI = useCallback(async (request) => {
        const otherUserId = request.senderUserId === currentUser?.uid ? request.targetUserId : request.senderUserId;
        const otherUserName = request.senderUserId === currentUser?.uid ? request.targetName : request.senderName;
        const companyId = getCompanyId();
        try {
            const permission = await introductionService.permission(companyId, otherUserId);
            if (!permission?.chatEnabled) { toast('Chat has not been enabled by the introduction recipient.', { icon: '🔒' }); return; }
            navigate('/dashboard/messages', { state: { userId: otherUserId, userName: otherUserName, fromAI: true, aiRequestId: request.id } });
        } catch (error) { toast.error(friendlyError(error, 'Could not verify chat permission.')); }
    }, [currentUser, navigate, getCompanyId]);

    const handleScheduleFromAI = useCallback(async (request) => {
        const otherUserId = request.senderUserId === currentUser?.uid ? request.targetUserId : request.senderUserId;
        const otherUserName = request.senderUserId === currentUser?.uid ? request.targetName : request.senderName;
        const companyId = getCompanyId();
        try {
            const permission = await introductionService.permission(companyId, otherUserId);
            if (!permission?.calendarShared) {
                const result = await introductionService.requestAvailability(companyId, {
                    targetUserId: otherUserId,
                    message: `I would like to schedule a business meeting with you. Please choose and share the dates and times that work for you.`,
                    agenda: 'Business discussion',
                    duration: 30,
                });
                toast.success(result?.alreadyRequested ? 'A meeting availability request is already pending.' : 'Availability request emailed to the user. They can choose the time windows they want to share.');
                return;
            }
            navigate('/dashboard/meetings/schedule', { state: { inviteUser: { id: otherUserId, uid: otherUserId, displayName: otherUserName, name: otherUserName, fromAI: true, aiRequestId: request.id } } });
        } catch (error) { toast.error(friendlyError(error, 'Could not verify calendar permission.')); }
    }, [currentUser, navigate, getCompanyId]);

    const handleViewMeeting = useCallback((meeting) => {
        setSelectedMeeting(meeting);
        setOpenSummaryDialog(true);
    }, []);

    const handleApproveMeeting = useCallback(async (meeting, remarks = '') => {
        setProcessing(true);
        try {
            const meetingRef = doc(db, 'meetings', meeting.id);
            await updateDoc(meetingRef, {
                status: 'confirmed',
                remarks: remarks || 'Approved',
                updatedAt: serverTimestamp()
            });

            await addDoc(collection(db, 'notifications'), {
                userId: meeting.requesterUserId || meeting.requesterId,
                type: 'meeting_approved',
                title: 'Meeting Approved',
                body: `${getCompanyName()} has approved your meeting request: "${meeting.title}"`,
                read: false,
                createdAt: serverTimestamp(),
                createdBy: currentUser?.uid || '',
                remarks: remarks || 'Approved',
            });

            toast.success(`Meeting "${meeting.title}" approved!`);
            fetchMeetings();
        } catch (error) {
            console.error('Error approving meeting:', error);
            toast.error('Failed to approve meeting');
        } finally {
            setProcessing(false);
        }
    }, [getCompanyName, fetchMeetings]);

    const handleRejectMeeting = useCallback(async (meeting, remarks = '') => {
        setProcessing(true);
        try {
            const meetingRef = doc(db, 'meetings', meeting.id);
            await updateDoc(meetingRef, {
                status: 'declined',
                remarks: remarks || 'Declined',
                updatedAt: serverTimestamp()
            });

            await addDoc(collection(db, 'notifications'), {
                userId: meeting.requesterUserId || meeting.requesterId,
                type: 'meeting_declined',
                title: 'Meeting Declined',
                body: `${getCompanyName()} has declined your meeting request: "${meeting.title}"`,
                read: false,
                createdAt: serverTimestamp(),
                createdBy: currentUser?.uid || '',
                remarks: remarks || 'Declined',
            });

            toast.info(`Meeting "${meeting.title}" declined`);
            fetchMeetings();
        } catch (error) {
            console.error('Error declining meeting:', error);
            toast.error('Failed to decline meeting');
        } finally {
            setProcessing(false);
        }
    }, [getCompanyName, fetchMeetings]);

    const handleSendMessage = useCallback(async (meeting, message) => {
        setProcessing(true);
        try {
            const meetingRef = doc(db, 'meetings', meeting.id);
            await updateDoc(meetingRef, {
                message: message,
                updatedAt: serverTimestamp()
            });

            const targetId = meeting.type === 'sent'
                ? (meeting.targetUserId || meeting.targetId)
                : (meeting.requesterUserId || meeting.requesterId);
            await addDoc(collection(db, 'notifications'), {
                userId: targetId,
                type: 'meeting_message',
                title: 'New Message',
                body: `${getCompanyName()} sent a message regarding "${meeting.title}"`,
                read: false,
                createdAt: serverTimestamp(),
                createdBy: currentUser?.uid || '',
                message: message,
                meetingId: meeting.id,
            });

            toast.success('Message sent successfully!');
            fetchMeetings();
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
        } finally {
            setProcessing(false);
        }
    }, [getCompanyName, fetchMeetings]);

    const handleCancelMeeting = useCallback((meeting) => {
        setSelectedMeeting(meeting);
        setOpenCancelDialog(true);
    }, []);

    const confirmCancelMeeting = useCallback(async () => {
        if (!selectedMeeting) return;
        const companyId = getCompanyId();
        setProcessing(true);
        try {
            await meetingWorkflowService.cancel(companyId, selectedMeeting.id, cancelReason || 'No reason provided');
            toast.success('Meeting cancelled and the other participant was notified.');
            setOpenCancelDialog(false);
            setCancelReason('');
            await fetchMeetings();
        } catch (error) {
            console.error('Error cancelling meeting:', error);
            toast.error(friendlyError(error, 'Failed to cancel meeting'));
        } finally { setProcessing(false); }
    }, [selectedMeeting, cancelReason, fetchMeetings, getCompanyId]);

    const handleManageTeam = useCallback((meeting) => {
        setSelectedMeeting(meeting);
        setOpenAddTeamDialog(true);
    }, []);

    const handleAddMembersToMeeting = useCallback(async (meeting, newMembers) => {
        try {
            const meetingRef = doc(db, 'meetings', meeting.id);
            const currentParticipants = (meeting.participants || []).map((participant) =>
                typeof participant === 'string'
                    ? { id: participant, userId: participant, name: participant, role: 'participant', type: 'user' }
                    : participant
            );
            const knownIds = new Set(currentParticipants.map(p => p?.id || p?.userId).filter(Boolean));
            const normalizedNewMembers = newMembers
                .filter(member => !knownIds.has(member.id || member.uid))
                .map(member => ({ ...member, id: member.id || member.uid, userId: member.uid || member.id }));
            const updatedParticipants = [...currentParticipants, ...normalizedNewMembers];
            const participantIds = Array.from(new Set([
                ...(meeting.participantIds || []),
                ...updatedParticipants.flatMap(p => [p?.id, p?.userId]).filter(Boolean),
            ]));
            
            await updateDoc(meetingRef, {
                participants: updatedParticipants,
                participantIds,
                updatedAt: serverTimestamp()
            });

            for (const member of newMembers) {
                await addDoc(collection(db, 'notifications'), {
                    userId: member.id,
                    type: 'meeting_invite',
                    title: 'Added to Meeting',
                    body: `You have been added to "${meeting.title}" by ${getCompanyName()}`,
                    read: false,
                    createdAt: serverTimestamp(),
                    createdBy: currentUser?.uid || '',
                    meetingId: meeting.id,
                    meetingTitle: meeting.title,
                });
            }

            toast.success(`${newMembers.length} team members added!`);
            fetchMeetings();
            setOpenAddTeamDialog(false);
        } catch (error) {
            console.error('Error adding members:', error);
            toast.error('Failed to add members');
            throw error;
        }
    }, [getCompanyName, fetchMeetings]);

    const handleScheduleNew = useCallback(() => {
        navigate('/dashboard/meetings/schedule');
    }, [navigate]);

    const handleInviteUser = useCallback((user) => {
        navigate('/dashboard/messages', {
            state: {
                targetUserId: user?.uid || user?.userId || user?.id,
                targetUser: user,
            },
        });
    }, [navigate]);

    const handleScheduleWithUser = useCallback((user) => {
        navigate('/dashboard/meetings/schedule', { 
            state: { inviteUser: user } 
        });
    }, [navigate]);

    // ── Listeners ─────────────────────────────────────────────────────────

    useEffect(() => {
        const companyId = getCompanyId();
        if (!companyId) {
            setLoading(false);
            return;
        }

        // Initial fetch
        fetchMeetings();
        fetchAIRequests();
        fetchAgoraConfig();

        // Meetings listeners - using both requesterId, targetId, and participantIds
        const meetingsRef = collection(db, 'meetings');
        
        const sentQ = query(meetingsRef, where('requesterId', '==', companyId));
        const unsubSent = onSnapshot(sentQ, 
            () => { fetchMeetings(); },
            (error) => { console.error('Sent meetings listener error:', error); }
        );

        const receivedQ = query(meetingsRef, where('targetId', '==', companyId));
        const unsubReceived = onSnapshot(receivedQ,
            () => { fetchMeetings(); },
            (error) => { console.error('Received meetings listener error:', error); }
        );

        const participantQ = query(meetingsRef, where('participantIds', 'array-contains', companyId));
        const unsubParticipant = onSnapshot(participantQ,
            () => { fetchMeetings(); },
            (error) => { console.error('Participant meetings listener error:', error); }
        );

        const legacyQ = query(meetingsRef, where('companyId', '==', companyId));
        const unsubLegacy = onSnapshot(legacyQ,
            () => { fetchMeetings(); },
            (error) => { console.error('Legacy meetings listener error:', error); }
        );

        // AI Requests listeners
        if (currentUser?.uid) {
            const aiRef = collection(db, 'ai_introductions');
            const aiIncomingQ = query(aiRef, where('targetUserId', '==', currentUser.uid));
            const unsubAiIncoming = onSnapshot(aiIncomingQ,
                () => { fetchAIRequests(); },
                (error) => { console.error('AI incoming listener error:', error); }
            );

            const aiOutgoingQ = query(aiRef, where('senderUserId', '==', currentUser.uid));
            const unsubAiOutgoing = onSnapshot(aiOutgoingQ,
                () => { fetchAIRequests(); },
                (error) => { console.error('AI outgoing listener error:', error); }
            );

            setUnsubscribeAiRequests(() => {
                unsubAiIncoming();
                unsubAiOutgoing();
            });
        }

        setUnsubscribeSent(() => unsubSent);
        setUnsubscribeReceived(() => unsubReceived);
        // Store participant unsubscribe
        const unsubParticipantRef = unsubParticipant;

        return () => {
            if (unsubSent) unsubSent();
            if (unsubReceived) unsubReceived();
            if (unsubParticipantRef) unsubParticipantRef();
            if (unsubLegacy) unsubLegacy();
            if (unsubscribeAiRequests) unsubscribeAiRequests();
        };
    }, [getCompanyId, fetchMeetings, fetchAIRequests, currentUser, fetchAgoraConfig]);

    // ── Derived Data ─────────────────────────────────────────────────────

    const statsData = useMemo(() => {
        const gradients = [
            [palette.rust, palette.rustGlow],
            [palette.teal, palette.tealGlow],
            [palette.amber, palette.amberGlow],
            [palette.pine, palette.pineGlow],
            [palette.plum, palette.plumGlow],
            [palette.indigo, palette.indigoGlow],
        ];
        return [
            {
                label: 'Total Meetings',
                value: stats.total,
                icon: <EventIcon />,
                overlayIcon: <EventIcon />,
                gradient: gradients[0],
                hint: 'All meetings',
            },
            {
                label: 'Upcoming',
                value: stats.upcoming,
                icon: <ScheduleIcon />,
                overlayIcon: <UpcomingIcon />,
                gradient: gradients[1],
                hint: 'Scheduled & confirmed',
            },
            {
                label: 'Pending',
                value: stats.pending,
                icon: <PendingIcon />,
                overlayIcon: <PendingIcon />,
                gradient: gradients[2],
                hint: 'Awaiting action',
            },
            {
                label: 'Completed',
                value: stats.completed,
                icon: <DoneIcon />,
                overlayIcon: <HistoryIcon />,
                gradient: gradients[3],
                hint: 'Finished meetings',
            },
            {
                label: 'AI Requests',
                value: stats.aiRequests,
                icon: <AiIcon />,
                overlayIcon: <ConnectIcon />,
                gradient: gradients[4],
                hint: 'Pending introductions',
            },
            {
                label: 'Requests',
                value: stats.received,
                icon: <InboxIcon />,
                overlayIcon: <RequestPageIcon />,
                gradient: gradients[5],
                hint: 'Incoming requests',
            },
        ];
    }, [stats]);

    const tabs = useMemo(() => [
        { label: 'Company Users', icon: <PeopleIcon />, count: null },
        { label: 'AI Introductions', icon: <ConnectIcon />, count: stats.aiRequests },
        { label: 'All', icon: <EventIcon />, count: stats.total },
        { label: 'Upcoming', icon: <ScheduleIcon />, count: stats.upcoming },
        { label: 'Pending', icon: <PendingIcon />, count: stats.pending },
        { label: 'Completed', icon: <DoneIcon />, count: stats.completed },
        { label: 'In Progress', icon: <LiveIcon />, count: stats.inProgress },
        { label: 'Requests', icon: <InboxIcon />, count: stats.received },
    ], [stats]);

    const companyId = getCompanyId();

    // ── Render ────────────────────────────────────────────────────────────

    return (
        <Box
            className="erp-page-shell erp-internal-page tf-meetings-page"
            sx={{
                minHeight: '100vh',
                position: 'relative',
                fontFamily: fontBody,
                color: palette.ink,
                background: `linear-gradient(180deg, ${palette.canvas} 0%, ${palette.canvasDeep} 100%)`,
                '&::before': {
                    content: '""',
                    position: 'fixed',
                    inset: 0,
                    zIndex: 0,
                    pointerEvents: 'none',
                    opacity: 0.4,
                    backgroundImage:
                        'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
                    animation: 'tfGrain 8s steps(6) infinite',
                },
            }}
        >
            <style>{FONT_IMPORT}</style>
            <Box sx={{ position: 'relative', zIndex: 2 }}><Header /></Box>

            <Container
                maxWidth={false}
                disableGutters
                sx={{ position: 'relative', zIndex: 1, px: { xs: 1.5, sm: 2.5, lg: 4 }, py: { xs: 2, md: 3 } }}
            >
                {/* ── hero ─────────────────────────────────────────────────── */}
                <MotionDiv
                    initial={{ opacity: 0, y: -12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box
                        className="tf-command-hero tf-meeting-hero"
                        sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            gap: 2, flexWrap: 'wrap', mb: 2.5,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 500,
                                    letterSpacing: '0.2em', textTransform: 'uppercase', color: 'inherit', opacity: 0.72, mb: 0.5,
                                }}
                            >
                                TelcoFair · Meeting Dashboard
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay, fontWeight: 600,
                                    fontSize: { xs: '1.35rem', md: '1.5rem' },
                                    letterSpacing: '-0.03em', lineHeight: 1.05, color: 'inherit',
                                }}
                            >
                                Meetings & Connections
                            </Typography>
                            <Typography sx={{ color: 'inherit', opacity: .78, fontSize: '0.78rem', mt: 0.5, maxWidth: 670 }}>
                                Manage meetings, AI introductions, and company connections
                            </Typography>
                            <Rule />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {stats.inProgress > 0 && (
                                <GlassPill
                                    label={`${stats.inProgress} In Progress`}
                                    color={palette.pine}
                                    icon={<LiveIcon sx={{ fontSize: 10 }} />}
                                    filled
                                />
                            )}
                            {stats.aiRequests > 0 && (
                                <GlassPill
                                    label={`${stats.aiRequests} AI Requests`}
                                    color={palette.plum}
                                    icon={<AiIcon sx={{ fontSize: 12 }} />}
                                    filled
                                />
                            )}
                            {stats.received > 0 && (
                                <GlassPill
                                    label={`${stats.received} Requests`}
                                    color={palette.amber}
                                    icon={<InboxIcon sx={{ fontSize: 12 }} />}
                                    filled
                                />
                            )}
                            <Button
                                startIcon={<AddIcon />}
                                onClick={handleScheduleNew}
                                sx={{
                                    px: 2, py: 0.8, borderRadius: '12px', textTransform: 'none', fontWeight: 700,
                                    fontSize: '0.78rem', color: getGradientContrastText(palette.rust, palette.rustDeep),
                                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                    border: `1px solid ${rgba('#FFFFFF', 0.25)}`,
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.35)}, 0 12px 24px -12px ${rgba(palette.rust, 1)}`,
                                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                                }}
                            >
                                Schedule Meeting
                            </Button>
                            <Button
                                startIcon={<RefreshIcon sx={{ animation: loading ? 'tfSpin 1s linear infinite' : 'none' }} />}
                                onClick={() => { fetchMeetings(); fetchAIRequests(); fetchAgoraConfig(); }}
                                sx={{
                                    px: 1.5, py: 0.8, borderRadius: '12px', textTransform: 'none', fontWeight: 600,
                                    fontSize: '0.72rem', color: palette.inkLight,
                                    border: `1px solid ${rgba('#FFFFFF', 0.7)}`,
                                    background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.8)}, ${rgba('#FFFFFF', 0.45)})`,
                                    backdropFilter: 'blur(14px)',
                                    '&:hover': { color: palette.ink, background: rgba('#FFFFFF', 0.9) },
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>
                </MotionDiv>

                <MeetingCommandCards navigate={navigate} agoraConfigured={Boolean(agoraConfig?.appId || agoraConfig?.enabled)} />

                {/* ── stats ─────────────────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 1.5,
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
                        mb: 3,
                    }}
                >
                    {statsData.map((s, i) => (
                        <ModernStatCard key={s.label} {...s} delay={i * 0.04} loading={loading} />
                    ))}
                </Box>

                {/* ── Search ─────────────────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1.5,
                        mb: 2.5,
                        alignItems: 'center',
                    }}
                >
                    <TextField
                        placeholder={tabValue === 0 ? "Search team members..." : "Search meetings..."}
                        value={searchTerm}
                        onChange={handleSearch}
                        fullWidth
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                ...glassWell(14),
                                fontSize: '0.85rem',
                                transition: 'all 0.3s ease',
                                '& fieldset': { border: 'none' },
                                '&.Mui-focused': {
                                    borderColor: alpha(palette.rust, 0.5),
                                    boxShadow: `inset 0 1px 6px rgba(0,0,0,0.06), 0 0 0 3px ${alpha(palette.rust, 0.14)}`,
                                },
                                '& input': { py: 1.3, color: palette.ink },
                            },
                        }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: palette.inkFaint, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={clearSearch}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }
                        }}
                    />

                    <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                        <Tooltip title="Grid View">
                            <IconButton
                                onClick={() => setViewMode('grid')}
                                sx={{
                                    ...glassWell(12),
                                    width: 40,
                                    height: 40,
                                    borderRadius: '12px',
                                    color: viewMode === 'grid' ? palette.rust : palette.inkFaint,
                                    bgcolor: viewMode === 'grid' ? rgba(palette.rust, 0.08) : 'transparent',
                                    border: `1px solid ${viewMode === 'grid' ? rgba(palette.rust, 0.2) : 'transparent'}`,
                                    '&:hover': { bgcolor: rgba(palette.rust, 0.05) },
                                }}
                            >
                                <GridViewIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="List View">
                            <IconButton
                                onClick={() => setViewMode('list')}
                                sx={{
                                    ...glassWell(12),
                                    width: 40,
                                    height: 40,
                                    borderRadius: '12px',
                                    color: viewMode === 'list' ? palette.rust : palette.inkFaint,
                                    bgcolor: viewMode === 'list' ? rgba(palette.rust, 0.08) : 'transparent',
                                    border: `1px solid ${viewMode === 'list' ? rgba(palette.rust, 0.2) : 'transparent'}`,
                                    '&:hover': { bgcolor: rgba(palette.rust, 0.05) },
                                }}
                            >
                                <ListIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* ── tabs ───────────────────────────────────────────────────── */}
                <Box
                    className="tf-dashboard-tabs"
                    sx={{
                        mb: 1.5,
                        ...liquid(palette.inkLight, { radius: 16, depth: 0.5 }),
                        p: 0.5,
                        background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.85)}, ${rgba('#FFFFFF', 0.5)})`,
                    }}
                >
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTabs-flexContainer': {
                                gap: 0.5,
                            },
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontFamily: fontBody,
                                fontSize: '0.78rem',
                                minHeight: 40,
                                borderRadius: '12px',
                                px: 1.5,
                                color: palette.inkLight,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    color: palette.ink,
                                    background: rgba(palette.ink, 0.04),
                                },
                            },
                            '& .Mui-selected': {
                                color: getGradientContrastText(palette.rust, palette.rustDeep),
                                background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                boxShadow: `0 4px 16px ${rgba(palette.rust, 0.35)}`,
                                '&:hover': {
                                    background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})`,
                                },
                            },
                            '& .MuiTabs-indicator': {
                                display: 'none',
                            },
                        }}
                    >
                        {tabs.map((tab, index) => (
                            <Tab
                                key={index}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        {tab.icon}
                                        {tab.label}
                                        {tab.count !== null && tab.count > 0 && (
                                            <Box
                                                sx={{
                                                    fontFamily: fontMono,
                                                    fontSize: '0.55rem',
                                                    fontWeight: 700,
                                                    color: tabValue === index ? '#fff' : palette.inkLight,
                                                    bgcolor: tabValue === index ? 'rgba(255,255,255,0.2)' : palette.hairline,
                                                    px: 0.6,
                                                    py: 0.1,
                                                    borderRadius: '6px',
                                                    minWidth: 18,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {tab.count}
                                            </Box>
                                        )}
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>
                </Box>

                {/* ── Content ────────────────────────────────────────────────── */}
                <GlassPanel className="tf-meeting-content-panel" accent={palette.rust} delay={0.05} lift={false} padding={{ xs: 1.25, md: 1.5 }}>
                    {tabValue === 0 ? (
                        // Company Users Tab
                        <CompanyUsersTab 
                            companyId={companyId} 
                            onInviteUser={handleInviteUser}
                            onSendAIIntro={handleSendAIIntro}
                            sentRequests={sentRequests}
                            onScheduleMeeting={handleScheduleWithUser}
                            meetings={meetings}
                            onAddUserToMeeting={(meeting, user) => handleAddMembersToMeeting(meeting, [user])}
                        />
                    ) : tabValue === 1 ? (
                        // AI Introductions Tab
                        <AIIntroRequestsTab 
                            requests={aiRequests}
                            onAccept={handleAcceptAIIntro}
                            onReject={handleRejectAIIntro}
                            onChat={handleChatFromAI}
                            onSchedule={handleScheduleFromAI}
                            currentUserId={currentUser?.uid}
                        />
                    ) : loading ? (
                        <Box sx={{ py: 2 }}>
                            {[1, 2, 3].map((i) => (
                                <Skeleton
                                    key={i}
                                    variant="rounded"
                                    height={100}
                                    sx={{ mb: 1.5, borderRadius: '18px', bgcolor: palette.hairline }}
                                />
                            ))}
                        </Box>
                    ) : error ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography sx={{ color: palette.danger, mb: 1 }}>⚠️ {error}</Typography>
                            <Button
                                onClick={() => { fetchMeetings(); fetchAIRequests(); fetchAgoraConfig(); }}
                                startIcon={<RefreshIcon />}
                                sx={{ color: palette.rust, fontWeight: 600 }}
                            >
                                Retry
                            </Button>
                        </Box>
                    ) : filteredMeetings.length === 0 ? (
                        <EmptyState
                            icon={<EventIcon />}
                            text={searchTerm ? 'No meetings match your search.' : 
                                  tabValue === 2 ? 'Schedule your first meeting to get started.' : 
                                  tabValue === 7 ? 'No pending meeting requests.' : 
                                  'No meetings in this category.'}
                            action={tabValue === 2 ? 'Schedule Meeting' : undefined}
                            onAction={tabValue === 2 ? handleScheduleNew : undefined}
                        />
                    ) : (
                        <Box 
                            sx={{ 
                                display: viewMode === 'grid' ? 'grid' : 'flex', 
                                flexDirection: viewMode === 'list' ? 'column' : 'unset',
                                gridTemplateColumns: viewMode === 'grid' ? { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' } : 'unset',
                                gap: 2 
                            }}
                        >
                            {filteredMeetings.map((meeting) => {
                                const meetingType = tabValue === 7 ? 'received' : undefined;
                                return (
                                    <MeetingCard
                                        key={meeting.id}
                                        meeting={meeting}
                                        viewMode={viewMode}
                                        onView={handleViewMeeting}
                                        onCancel={handleCancelMeeting}
                                        onJoin={handleJoinMeeting}
                                        onApprove={handleApproveMeeting}
                                        onReject={handleRejectMeeting}
                                        onManageTeam={handleManageTeam}
                                        type={meetingType || meeting.type || 'sent'}
                                        companyDirectory={companyDirectory}
                                        userDirectory={userDirectory}
                                        currentCompanyId={getCompanyId() || ''}
                                        currentUserId={currentUser?.uid || ''}
                                    />
                                );
                            })}
                        </Box>
                    )}
                </GlassPanel>
            </Container>

            {/* ── Dialogs ────────────────────────────────────────────────────── */}

            {/* View Summary Dialog - Simplified version for brevity */}
            <Dialog
                open={openSummaryDialog}
                onClose={() => setOpenSummaryDialog(false)}
                maxWidth="lg"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            ...liquid(toneFor(selectedMeeting?.status || 'pending'), { radius: 28 }),
                            background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 60%, ${rgba(toneFor(selectedMeeting?.status || 'pending'), 0.06)})`,
                            maxHeight: '90vh',
                            overflow: 'hidden',
                        }
                    },
                    backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: rgba(palette.ink, 0.28) } }
                }}
            >
                <Box
                    sx={{
                        p: 2.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.25 }}>
                        <Box sx={{ position: 'relative' }}>
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: '14px',
                                    display: 'grid',
                                    placeItems: 'center',
                                    color: getContrastText(toneFor(selectedMeeting?.status || 'pending')),
                                    background: `linear-gradient(150deg, ${rgba(toneFor(selectedMeeting?.status || 'pending'), 0.9)}, ${rgba(toneFor(selectedMeeting?.status || 'pending'), 0.6)})`,
                                    boxShadow: `0 8px 24px ${rgba(toneFor(selectedMeeting?.status || 'pending'), 0.35)}`,
                                }}
                            >
                                <EventIcon sx={{ fontSize: 26 }} />
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                                {selectedMeeting?.title || 'Untitled Meeting'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                                <GlassPill
                                    label={selectedMeeting?.type === 'received' ? `From: ${selectedMeeting?.requesterCompany}` : `To: ${selectedMeeting?.targetCompany}`}
                                    color={palette.inkLight}
                                    size="sm"
                                />
                                {selectedMeeting?.isAI && (
                                    <GlassPill label="AI Generated" color={palette.plum} size="sm" icon={<AiIcon sx={{ fontSize: 10 }} />} />
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <MeetingStatusChip status={selectedMeeting?.status} />
                        <IconButton
                            onClick={() => setOpenSummaryDialog(false)}
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                color: getContrastText(palette.danger),
                                bgcolor: palette.danger,
                                boxShadow: `0 4px 16px ${rgba(palette.danger, 0.4)}`,
                                '&:hover': {
                                    bgcolor: palette.danger,
                                    transform: 'rotate(90deg)',
                                },
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </Box>

                <DialogContent sx={{ px: 2.75, py: 2.5 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                        {[
                            { icon: <CalendarIcon />, label: 'Date', value: formatDateTime(selectedMeeting?.scheduledTime), color: palette.rust },
                            { icon: <AccessTimeIcon />, label: 'Duration', value: `${selectedMeeting?.duration || 30} min`, color: palette.teal },
                            { icon: <PeopleIcon />, label: 'Participants', value: `${selectedMeeting?.participants?.length || 2} people`, color: palette.pine },
                            { icon: <LocationIcon />, label: 'Location', value: selectedMeeting?.location || 'Virtual', color: palette.amber },
                            { icon: <EventIcon />, label: 'Status', value: selectedMeeting?.status?.charAt(0).toUpperCase() + selectedMeeting?.status?.slice(1) || 'Pending', color: toneFor(selectedMeeting?.status || 'pending') },
                            { icon: <PublicIcon />, label: 'Meeting ID', value: selectedMeeting?.id?.slice(0, 8) || 'N/A', color: palette.plum },
                        ].map((card, index) => (
                            <MotionDiv
                                key={index}
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        ...glassWell(14),
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '11px',
                                            display: 'grid',
                                            placeItems: 'center',
                                            color: getContrastText(card.color),
                                            background: `linear-gradient(150deg, ${rgba(card.color, 0.9)}, ${rgba(card.color, 0.6)})`,
                                            boxShadow: `0 4px 12px ${rgba(card.color, 0.3)}`,
                                            mb: 1,
                                        }}
                                    >
                                        {React.cloneElement(card.icon, { sx: { fontSize: 18 } })}
                                    </Box>
                                    <Typography sx={{ color: palette.inkLight, fontSize: '0.58rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                        {card.label}
                                    </Typography>
                                    <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '0.95rem', color: palette.ink, mt: 0.25 }}>
                                        {card.value}
                                    </Typography>
                                </Paper>
                            </MotionDiv>
                        ))}
                    </Box>

                    {selectedMeeting?.message && (
                        <Paper elevation={0} sx={{ p: 2.5, ...glassWell(16), mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                <GlassIcon icon={<MessageIcon />} color={palette.amber} size={32} />
                                <Typography sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.85rem' }}>
                                    Message
                                </Typography>
                            </Box>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.85rem', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                {selectedMeeting.message}
                            </Typography>
                        </Paper>
                    )}

                    {selectedMeeting?.agenda && (
                        <Paper elevation={0} sx={{ p: 2.5, ...glassWell(16), mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                <GlassIcon icon={<ScheduleIcon />} color={palette.teal} size={32} />
                                <Typography sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.85rem' }}>
                                    Agenda
                                </Typography>
                            </Box>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.85rem', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                {selectedMeeting.agenda}
                            </Typography>
                        </Paper>
                    )}
                </DialogContent>

                <Box
                    sx={{
                        px: 2.75,
                        py: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1,
                        borderTop: `1px solid ${palette.hairlineSoft}`,
                        background: rgba('#FFFFFF', 0.45),
                        backdropFilter: 'blur(8px)',
                        flexWrap: 'wrap',
                    }}
                >
                    <GlassSolidButton
                        label="Close"
                        color={palette.inkLight}
                        icon={<CloseIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setOpenSummaryDialog(false)}
                        size="medium"
                    />
                </Box>
            </Dialog>

            {/* Add Team Members Dialog */}
            <AddTeamMembersDialog
                open={openAddTeamDialog}
                onClose={() => setOpenAddTeamDialog(false)}
                meeting={selectedMeeting}
                onAddMembers={handleAddMembersToMeeting}
            />

            {/* Cancel Meeting Dialog */}
            <Dialog
                open={openCancelDialog}
                onClose={() => setOpenCancelDialog(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            ...liquid(palette.danger, { radius: 24 }),
                            background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 60%, ${rgba(palette.danger, 0.06)})`,
                        },
                    },
                    backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: rgba(palette.ink, 0.28) } }
                }}
            >
                <Box
                    sx={{
                        p: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                        <GlassIcon icon={<CancelIcon />} color={palette.danger} size={44} />
                        <Box>
                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.1rem', color: palette.danger }}>
                                Cancel Meeting
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.72rem' }}>
                                This action cannot be undone
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setOpenCancelDialog(false)}
                        sx={{
                            width: 32, height: 32, borderRadius: '10px', color: palette.inkSoft,
                            bgcolor: rgba(palette.ink, 0.04),
                            '&:hover': { bgcolor: rgba(palette.ink, 0.08), transform: 'rotate(90deg)' },
                            transition: 'transform .3s ease, background .3s ease',
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>

                <DialogContent sx={{ px: 2.5, py: 2.5 }}>
                    <Typography sx={{ color: palette.ink, mb: 2 }}>
                        Are you sure you want to cancel this meeting?
                    </Typography>
                    <TextField
                        fullWidth
                        label="Reason for cancellation (optional)"
                        multiline
                        rows={3}
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        sx={{
                            ...glassWell(12),
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                bgcolor: 'transparent',
                            }
                        }}
                    />
                </DialogContent>

                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1,
                        borderTop: `1px solid ${palette.hairlineSoft}`,
                        background: rgba('#FFFFFF', 0.45),
                        backdropFilter: 'blur(8px)',
                    }}
                >
                    <Button
                        onClick={() => setOpenCancelDialog(false)}
                        sx={{ textTransform: 'none', fontWeight: 600, color: palette.inkSoft, borderRadius: '10px', px: 1.75, fontSize: '0.78rem' }}
                    >
                        Keep Meeting
                    </Button>
                    <GlassSolidButton
                        label={processing ? 'Cancelling...' : 'Cancel Meeting'}
                        color={palette.danger}
                        icon={processing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CancelIcon sx={{ fontSize: 14 }} />}
                        onClick={confirmCancelMeeting}
                        disabled={processing}
                    />
                </Box>
            </Dialog>

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: .45; transform: scale(.82); }
                }
                @keyframes tfRule {
                    0%, 100% { opacity: .7; transform: scaleX(1); }
                    50%      { opacity: 1;  transform: scaleX(1.35); }
                }
                @keyframes tfGrain {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(-2%, 1%); }
                    50%  { transform: translate(1%, -2%); }
                    75%  { transform: translate(-1%, -1%); }
                    100% { transform: translate(0, 0); }
                }
                @keyframes tfOrbFloat {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    50%      { transform: translate(-10px, -10px) scale(1.1); }
                }
                @keyframes tfSpin { to { transform: rotate(360deg); } }
                * { scrollbar-width: thin; scrollbar-color: ${rgba(palette.ink, 0.14)} transparent; }
                *::-webkit-scrollbar { width: 5px; height: 5px; }
                *::-webkit-scrollbar-track { background: transparent; }
                *::-webkit-scrollbar-thumb {
                    background: ${rgba(palette.ink, 0.12)};
                    border-radius: 999px;
                }
                *::-webkit-scrollbar-thumb:hover { background: ${rgba(palette.ink, 0.22)}; }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: .001ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .001ms !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default MeetingDashboard;